import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, Navigate, useSearchParams } from 'react-router-dom';
import RefLink from '../components/shared/RefLink';
import {
  Plus, Pencil, Trash2, X, AlertTriangle,
  ChevronLeft, ChevronRight, Calendar, UserCheck, Award,
  MessageSquare, GraduationCap, Search,
  QrCode, Copy, RefreshCw, CheckCircle2,
  FileSignature, ListChecks, History, UserPlus, Eye, Lock, Megaphone,
  FileBadge, MapPin, MapPinOff, ArrowLeft, ClipboardCheck, FolderOpen,
} from 'lucide-react';
// Course materials and recording — L&D's door (trainers have theirs in My Records).
import SessionFilesModal from '../components/lnd/SessionFilesModal';
import QRCode from 'qrcode';
import { useAuth } from '../contexts/AuthContext';
import SortableHeader, { PAGE_SIZE } from '../components/SortableTable';
import SearchSelect from '../components/shared/SearchSelect';
import { FilePicker, InfoTip, inputCls, lockedInputCls, LockTip } from '../components/shared/FormKit';
import FileList, { PreviousVersions } from '../components/shared/FileList';
import ResultCount from '../components/shared/ResultCount';
import WorkflowRefLink from '../components/shared/WorkflowRefLink';
import { useCpdAutofill, MAX_CERTIFICATE_PAGES } from '../components/workflow/CpdAutofill';
// One editable row per certificate — the very list the employee declaration
// form renders, so a PDF holding two certificates is split the same way on
// both screens.
import CpdCertificateRows, {
  useCpdRows, cpdRowError, cpdRowsGaps, describeCpdRows,
} from '../components/workflow/CpdCertificateRows';
import {
  FormValidationProvider, useFormValidation, useFieldInvalid, INVALID_FIELD_ATTR,
} from '../components/shared/formValidation';
import { fieldInvalidRing, fieldInvalidLabel, fieldInvalidText, subsectionTitle, subsectionTitleBar } from '../components/workflow/constants';
import AudienceRulesEditor from '../components/lnd/AudienceRulesEditor';
import { leadTrainerNames, assistantTrainers } from '../components/lnd/trainerUtils';
import { audienceRulesPayload, summariseAudienceRules } from '../components/lnd/audienceRuleMeta';
// GPS verdict of a QR scan — the same badge every surface shows.
import VenueCheckBadge from '../components/lnd/VenueCheckBadge';
// The evidence behind one verdict, opened by clicking that badge. Shared with
// the dashboard's Participation Records tab so a disputed check-in reads the
// same wherever it is opened from.
import AttendanceLocationModal from '../components/lnd/AttendanceLocationModal';
// The same panel the dashboard's Training Sessions tab expands into — the
// row's View action opens it in a modal.
import SessionDetailPanel from '../components/lnd/SessionDetailPanel';
// Shared with the session detail panel — one look for the four head counts.
import StatTile, { SESSION_STAT_HINTS } from '../components/lnd/StatTile';
// The yearly hours target and training year — a form, not a record list, so the
// Training Settings tab renders it instead of the table (see `panel` below).
import TrainingSettingsPanel from '../components/lnd/TrainingSettingsPanel';
import CpdFlagChips from '../components/lnd/CpdFlagChips';
import CpdSpotCheckModal from '../components/lnd/CpdSpotCheckModal';
import { CPD_FLAG_META, CPD_FLAG_CODES, unconfirmedFlags } from '../components/lnd/cpdFlags';
import { formatDate, formatDateTime, formatDateShort } from '../utils/dateFormat';
import { EFFECTIVENESS_LABELS, COMPETENCY_LABELS } from '../utils/feedbackScales';
// The shared helper, not a local copy (workflow-ui.md §kit). A local copy that
// sets 'Content-Type: application/json' on EVERY request silently breaks every
// multipart upload from this page, because the browser then never writes the
// multipart boundary.
import { fetchJSON, API_BASE_URL } from '../utils/api';
// The small "Mandatory" pill next to a session's category — the flag is
// its own column in the data, so the list marks it rather than a category row.
const MandatoryBadge = () => (
  <span className="inline-flex items-center rounded-full bg-blue-100 px-2 py-0.5 text-[11px] font-medium text-blue-700">
    Mandatory
  </span>
);
// A session freezes when HR presses Mark as completed — never automatically.
// Enforced server-side with 409 SESSION_COMPLETED; this only keeps the
// buttons honest.
const isCompletedSession = (r) => !!r.completed;

// Key-sorted JSON, for comparing a form value against its stored copy (jsonb
// returns object keys in its own order).
const canonJson = (v) => JSON.stringify(v, (_k, val) => (
  val && typeof val === 'object' && !Array.isArray(val)
    ? Object.keys(val).sort().reduce((acc, k) => { acc[k] = val[k]; return acc; }, {})
    : val
));
const THEAD_CLS = 'px-4 py-2 text-xs font-semibold text-slate-500 uppercase';

// How an attendance row got filed. Four ways in, and a reader
// treats them differently: a scan is the person's own word, a manual entry is
// HR's, a dispute is a decision (so it links back to it), and the trainer's own
// booking was made by the system when the session was marked completed.
const ATTENDANCE_SOURCES = {
  checkin: { label: 'QR check-in', cls: 'bg-green-50 text-green-700' },
  manual: { label: 'Manual entry', cls: 'bg-slate-100 text-slate-600' },
  completion: { label: 'Session completed', cls: 'bg-blue-50 text-blue-700' },
  'workflow:dispute': { label: 'Dispute approved', cls: 'bg-amber-50 text-amber-700' },
};
const renderAttendanceSource = (r) => {
  const meta = ATTENDANCE_SOURCES[r.source] || ATTENDANCE_SOURCES.manual;
  const badge = (
    <span className={`px-1.5 py-0.5 text-xs rounded whitespace-nowrap ${meta.cls}`}>{meta.label}</span>
  );
  if (!r.sourceRequestNo) return badge;
  return (
    <span className="flex items-center gap-1.5">
      {badge}
      <RefLink
        to={`/workflow/dispute/${r.sourceRequestId}`}
        onClick={(e) => e.stopPropagation()}
        className="text-xs text-blue-600 hover:text-blue-800"
      >
        {r.sourceRequestNo}
      </RefLink>
    </span>
  );
};
// The catch-all of the Absence Reason list. Picking it says
// nothing on its own, so the Details field turns required — server-side too.
const ABSENCE_REASON_OTHER = 'Others';
// Check-in modal refresh interval while HR watches people scan in.
const CHECKIN_POLL_MS = 5000;

// The letters a session can send. Same picker, same log;
// only the audience and the wording differ.
const MAIL_ACTIONS = [
  {
    kind: 'announcement',
    label: 'Course Notice',
    icon: Megaphone,
  },
  {
    kind: 'absence_reminder',
    label: 'Absence Reminder',
    icon: AlertTriangle,
  },
  {
    kind: 'feedback_request',
    label: 'Feedback Request',
    icon: MessageSquare,
  },
  // Always shown, but greyed out until the session carries a quiz
  // (`needsQuiz`) so HR can see the letter exists.
  {
    kind: 'quiz_request',
    label: 'Quiz Reminder',
    icon: ClipboardCheck,
    needsQuiz: true,
  },
];
const MAIL_LABELS = MAIL_ACTIONS.reduce((acc, a) => { acc[a.kind] = a.label; return acc; }, {});
const MAIL_PICKER_HINT = {
  announcement: 'Everyone on the expected list. Ticked by default: anyone not yet sent the notice.',
  absence_reminder: 'Expected attendees who have not checked in. Anyone who already filed an absence reason is greyed out.',
  feedback_request: 'People who attended. Anyone whose feedback is already in is greyed out.',
  quiz_request: 'The quiz audience. Anyone who has already passed the quiz is greyed out; Not passed is chased again.',
};

// Dates arrive as YYYY-MM-DD (or longer ISO strings) — keep YYYY-MM-DD only.
// FORM VALUES ONLY (<input type="date"> needs ISO). Anything the user reads
// goes through formatDate/formatDateTime → DD-MM-YYYY.
const toDateInput = (v) => (v ? String(v).slice(0, 10) : '');
const yesNo = (v) => (v ? 'Yes' : 'No');

const RATING_OPTIONS = [1, 2, 3, 4, 5];

// Wording comes from utils/feedbackScales.js — the same labels the employee
// form and every read-side surface use, so one score reads the same everywhere.
const RATING_SCALES = {
  effectiveness: {
    low: EFFECTIVENESS_LABELS[1],
    high: EFFECTIVENESS_LABELS[5],
    labels: EFFECTIVENESS_LABELS,
  },
  competency: {
    low: COMPETENCY_LABELS[1],
    high: COMPETENCY_LABELS[5],
    labels: COMPETENCY_LABELS,
  },
};

// ── Start/End time ────────────────────────────────────────────────────────────
// ld_training_sessions.start_end_time is one free-text column holding a range
// ('9:00 AM - 12:00 PM', legacy rows also '10:00:00 AM - 4:30:00 PM'). The form
// edits it with two <input type="time"> pickers, so the two halves are parsed
// out on the way in and re-composed on the way out — the stored shape and every
// read path (dashboard, exports) stay exactly as they were.
const TIME_RANGE_SEP = ' - ';

// '4:30:00 PM' | '16:30' | '9 AM' → 'HH:MM' for the time input ('' if unparsable).
const toTimeInput = (raw) => {
  if (!raw) return '';
  const m = String(raw).trim().match(/^(\d{1,2})(?::(\d{2}))?(?::\d{2})?\s*([AaPp][Mm])?$/);
  if (!m) return '';
  let hour = parseInt(m[1], 10);
  const minute = m[2] || '00';
  const meridiem = m[3] ? m[3].toUpperCase() : null;
  if (meridiem === 'PM' && hour < 12) hour += 12;
  if (meridiem === 'AM' && hour === 12) hour = 0;
  if (hour > 23) return '';
  return `${String(hour).padStart(2, '0')}:${minute}`;
};

// 'HH:MM' → '9:00 AM' (the format the column has always stored).
const fromTimeInput = (hhmm) => {
  if (!hhmm) return '';
  const [h, m] = hhmm.split(':');
  const hour = parseInt(h, 10);
  if (isNaN(hour)) return '';
  const meridiem = hour >= 12 ? 'PM' : 'AM';
  const hour12 = hour % 12 === 0 ? 12 : hour % 12;
  return `${hour12}:${m} ${meridiem}`;
};

const parseTimeRange = (value) => {
  const parts = String(value || '').split(/\s*-\s*/);
  return { start: toTimeInput(parts[0]), end: toTimeInput(parts[1]) };
};

// 'HH:MM' × 2 → the duration in hours as a form string ('' when a half is
// missing or the range does not run forwards — an end before the start is a
// typo far more often than a real overnight course, so nothing is derived and
// the Hours already in the form is left alone).
const durationHours = (start, end) => {
  if (!start || !end) return '';
  const minutes = (hhmm) => {
    const [h, m] = hhmm.split(':');
    return parseInt(h, 10) * 60 + parseInt(m, 10);
  };
  const diff = minutes(end) - minutes(start);
  if (!(diff > 0)) return '';
  return String(Math.round((diff / 60) * 100) / 100);
};

// Either half alone is kept as-is so a partially filled form is not silently
// turned into a bogus range.
const formatTimeRange = (start, end) => {
  const s = fromTimeInput(start);
  const e = fromTimeInput(end);
  if (s && e) return `${s}${TIME_RANGE_SEP}${e}`;
  return s || e || '';
};

// Does the qualification picked on the Qualifications form lapse? The single
// answer behind both the Expires On field's existence and its star —
// the catalogue's `requiresRenewal`, exactly as the Workflow declaration reads
// it. A type since retired from the picker is not on the options feed, so the
// row's own flag (JOIN_SQL.qualifications) answers for the record being edited.
const qualificationLapses = (form, ctx) => {
  const picked = ctx?.qualTypeOptions?.find(q => String(q.id) === String(form.qualificationTypeId));
  if (picked) return !!picked.requiresRenewal;
  return !!ctx?.record?.requiresRenewal
    && String(form.qualificationTypeId) === String(ctx.record.qualificationTypeId);
};

// The ten tabs are split across three screens — one bar of ten is not
// scannable. Same component serves all three: the route
// picks the group, the tab bar shows that group's tabs, and everything below
// (forms, modals, history, QR check-in) is shared exactly as before.
// `?tab=` deep links keep working from any of the three URLs — a tab belonging
// to another group redirects to its own screen (see LDRecordsPage at the end).
const TAB_GROUPS = [
  {
    key: 'training',
    path: '/ld/records',
    title: 'Training Records',
    description: 'Training sessions and what came out of each one — attendance, absences, feedback and External CPD.',
  },
  {
    key: 'development',
    path: '/ld/records/development',
    title: 'Staff Development',
    description: 'What each staff member holds: professional qualifications and firm-sponsored training agreements.',
  },
  {
    key: 'setup',
    path: '/ld/setup',
    title: 'L&D Setup',
    description: 'The lists the L&D forms pick from — trainers, qualification types, training categories and venues — plus the yearly mandatory-hours target and the training year.',
  },
];
const getGroupConfig = (key) => TAB_GROUPS.find(g => g.key === key) || TAB_GROUPS[0];

// Record type configuration — three screens, ten record resources of the
// /api/ld/records/:type generic pattern. `group` says which screen a tab is on.
//
// Field types: text | textarea | number | date | checkbox | rating |
//              select (static options) | lookup (managed Dropdown List, see
//              LOOKUP_FIELD_CATEGORIES) | timerange (two time pickers stored as
//              one 'start - end' string) | staff (active-staff select) |
//              session (training-session select) | audience (expected-attendee
//              rules)
// `identity: true` fields are fixed after creation (shown read-only in edit).
// `requiredIf: (form, ctx) => bool` = conditionally required (Absences →
// Details once the reason is the 'Others' catch-all; Qualifications → Expires
// On once the picked qualification needs renewing). `ctx` = { qualTypeOptions,
// record } for the rules that have to read outside the form.
// `showIf: (form, ctx) => bool` = the field only exists for certain answers
// (Qualifications → Expires On). A hidden field is saved as null, so an answer
// that stops applying never leaves a stale value behind.
// `filters` declares which server-side list filters the tab offers — only
// where the record actually carries that FK (the API ignores the rest).
// `completion`, `category` and `mandatory` are Sessions' own.
const RECORD_TABS = [
  {
    key: 'sessions',
    group: 'training',
    label: 'Sessions',
    icon: Calendar,
    title: 'training session',
    // Session Code is off the list (it is an internal id nobody reads), and
    // there is no Division column — the audience is a set of rules that need
    // not involve departments at all, so a derived department column would say
    // little and mislead when empty. Who is actually expected lives behind the
    // row's View action.
    columns: [
      { key: 'courseTitle', label: 'Course Title' },
      // Two columns, not one cell: the classification is two independent
      // answers, and a pill riding inside Category would make the mandatory
      // flag look like a property of the category. '—' = a legacy
      // non-mandatory row that has not been re-saved (the form will insist on
      // a category when it is).
      {
        key: 'category',
        label: 'Category',
        render: (r) => r.categoryTitle || <span className="text-slate-400">—</span>,
      },
      // Its own column, sortable and filterable — this is the only thing the
      // 40-hour compliance figures hang off.
      {
        key: 'isMandatory',
        label: 'Mandatory',
        render: (r) => (r.isMandatory
          ? <MandatoryBadge />
          : <span className="text-xs text-slate-500">Non-mandatory</span>),
      },
      { key: 'date', label: 'Date', render: (r) => formatDateShort(r.date) },
      { key: 'startEndTime', label: 'Time' },
      { key: 'hours', label: 'Hours', align: 'right' },
      // Where it is held. Blank is a real answer — off-site, online, or
      // a session recorded before the venue list existed.
      {
        key: 'venue',
        label: 'Venue',
        render: (r) => r.venueName || <span className="text-slate-400">—</span>,
      },
      // Who runs it: the lead trainers by name, assistants as a count — the
      // session's detail panel lists everyone with their factor.
      {
        key: 'trainers',
        label: 'Trainers',
        render: (r) => {
          const leads = leadTrainerNames(r.trainers);
          const assistants = assistantTrainers(r.trainers).length;
          if (!leads && assistants === 0) return <span className="text-slate-400">—</span>;
          return (
            <span className="block max-w-[200px]">
              <span className="block truncate text-sm text-slate-900" title={leads || ''}>{leads || '—'}</span>
              {assistants > 0 && (
                <span className="block text-xs text-slate-500">
                  +{assistants} assistant{assistants === 1 ? '' : 's'}
                </span>
              )}
            </span>
          );
        },
      },
      // The post-course quiz: the attached paper, or — when L&D left it to
      // the trainers and they have not written it yet — "Pending (trainer)".
      // Blank = no quiz for this session.
      {
        key: 'quiz',
        label: 'Quiz',
        render: (r) => (r.quizTitle
          ? (
            <span className="block truncate max-w-[160px] text-xs text-slate-700" title={r.quizTitle}>
              {r.quizTitle}
            </span>
          )
          : r.quizMode === 'trainer'
            ? <span className="inline-flex px-1.5 py-0.5 rounded bg-amber-50 text-amber-700 text-[11px] font-medium whitespace-nowrap">Pending (trainer)</span>
            : <span className="text-slate-400">—</span>),
      },
      // Where a session stands. Completed = frozen for good; check-in
      // open = people are scanning in right now; otherwise it is live working
      // data whose hours still flow through to everyone booked on it.
      {
        key: 'status',
        label: 'Status',
        render: (r) => (
          isCompletedSession(r) ? (
            <span className="text-xs font-medium text-slate-600">Completed</span>
          ) : r.checkinOpen ? (
            <span className="text-xs font-medium text-green-700">
              Check-in open
            </span>
          ) : (
            <span className="text-xs text-slate-600">Open</span>
          )
        ),
      },
    ],
    fields: [
      // Session Code is deliberately absent: the API generates it from the
      // course title and date (Course_Title_M/D/YYYY) and it is the legacy id
      // attendance/QR links hang off — never hand-edited.
      //
      // The title is free text. The classification is two separate answers, in
      // this order: mandatory or not first — the only thing the 40-hour
      // compliance figures hang off — then the category, which every session
      // takes regardless.
      { key: 'courseTitle', label: 'Course', type: 'text', required: true, placeholder: 'e.g. Annual IFRS Update 2026' },
      {
        key: 'isMandatory',
        label: 'Mandatory training?',
        type: 'mandatoryFlag',
        required: true,
      },
      {
        key: 'categoryId',
        label: 'Category',
        type: 'category',
        required: true,
      },
      // `lockWhileCheckinOpen` = frozen once people are scanning in: moving
      // the sitting or re-cutting the expected list would
      // rewrite what those check-ins mean. The API enforces it too (409
      // CHECKIN_OPEN) — this only keeps the form honest.
      { key: 'date', label: 'Date', type: 'date', required: true, lockWhileCheckinOpen: true },
      // `derives` = picking both halves fills that field in with the duration.
      {
        key: 'startEndTime',
        label: 'Start/End Time',
        type: 'timerange',
        derives: 'hours',
        required: true,
        lockWhileCheckinOpen: true,
      },
      // Not locked while check-in is open: changing the hours
      // recalculates every attendance row on the session, so the figure and the
      // rows can never drift apart.
      {
        key: 'hours',
        label: 'Hours',
        type: 'number',
        required: true,
      },
      // Who is expected. Department is one rule type among ten.
      {
        key: 'audienceRules',
        label: 'Expected Attendees',
        type: 'audience',
        lockWhileCheckinOpen: true,
        lookups: ['staff_department', 'staff_designation'],
        hint: 'Editing rules here rebuilds the expected list when you save.',
      },
      // Who teaches it — any number, roster-restricted, each with a role
      // (lead / assistant) and their own factor: trainer hours = session
      // hours × factor, booked when the session is marked completed. 0 is a
      // real answer (listed, books no hours). Leads are who the lists and
      // letters name, so whenever anyone is listed one must be the lead.
      {
        key: 'trainers',
        label: 'Trainers',
        type: 'trainers',
        hint: 'Each trainer earns session hours × their own factor. 1 = the session’s own hours, 0 = listed but no hours.',
      },
      // Optional on purpose — an online or off-site session has
      // no room on the list, and leaving it blank must stay a valid answer
      // rather than forcing a fake venue onto the record.
      {
        key: 'venueId',
        label: 'Venue',
        type: 'venue',
      },
      // Post-course quiz — first whether there is one, then where it comes
      // from: a Quiz Bank paper picked here, or the trainers writing it on the
      // session page. Everyone expected sits it; an absentee who passes is
      // credited the hours (docs/modules/ld.md §5.3 / §5.7).
      {
        key: 'quizMode',
        label: 'Post-Course Quiz',
        type: 'quizMode',
        newDefault: 'none',
      },
      {
        key: 'quizId',
        label: 'Quiz',
        type: 'quiz',
        hint: 'Papers are written in Management → Quiz Bank.',
        showIf: (form) => form.quizMode === 'bank',
      },
    ],
    filters: { completion: true, category: true, mandatory: true },
  },
  {
    key: 'attendance',
    group: 'training',
    label: 'Attendance',
    icon: UserCheck,
    title: 'attendance record',
    columns: [
      { key: 'session', label: 'Session', render: (r) => r.courseTitle || r.sessionCode || `#${r.sessionId}` },
      { key: 'staff', label: 'Staff', render: (r) => r.staffName || `#${r.staffId}` },
      { key: 'trainingHours', label: 'Training Hours', align: 'right' },
      { key: 'trainerHours', label: 'Trainer Hours', align: 'right' },
      // When the row was written — for a QR scan that is the moment the person
      // scanned, which is the whole point of asking.
      {
        key: 'createdAt',
        label: 'Recorded At',
        render: (r) => (r.createdAt
          ? formatDateTime(r.createdAt)
          : <span className="text-slate-400">—</span>),
      },
      // How it got here. Four ways in, and they mean different things to
      // whoever is reading the list.
      { key: 'source', label: 'Source', render: (r) => renderAttendanceSource(r) },
      // The GPS verdict of a QR scan. Three-valued, and the dash is the
      // common case — every non-scan source, and scans without a reading.
      // Clicking a verdict opens the evidence behind it —
      // coordinates, accuracy, the venue it was measured against. The same
      // panel opens from the dashboard's Participation Records tab for
      // anyone who also holds ld:training:edit (the endpoint's own bar); the
      // session panel still shows the distance and stops there.
      {
        key: 'venueCheck',
        label: 'Venue Check',
        render: (r, ctx) => (
          <VenueCheckBadge
            value={r.checkinInRange}
            distanceM={r.checkinDistanceM}
            radiusM={r.checkinRadiusM}
            onClick={() => ctx.onShowLocation(r.id)}
          />
        ),
      },
    ],
    fields: [
      { key: 'sessionId', label: 'Session', type: 'session', required: true, identity: true },
      { key: 'staffId', label: 'Staff', type: 'staff', required: true, identity: true },
      { key: 'trainingHours', label: 'Training Hours', type: 'number', required: true },
      { key: 'trainerHours', label: 'Trainer Hours', type: 'number' },
    ],
    // Session first, then the staff list scoped to that session (see
    // RecordModal). Hours come from the session and are read-only. The trainer
    // is not on that list at all — their row is written when
    // the session is marked completed, so Trainer Hours is not on the New form.
    //
    // `multiCreate`: registering attendance is a per-session
    // job, not a per-person one, so New takes the whole list in one sitting and
    // files it through the batch endpoint.
    // `settled` leaves out anyone the session has already answered for — filed
    // attendance, or an approved absence — since picking them could only ever
    // come back as a duplicate.
    roster: {
      sessionField: 'sessionId',
      staffField: 'staffId',
      hours: { training: 'trainingHours', trainer: 'trainerHours' },
      multiCreate: true,
      excludeSettled: true,
    },
    duplicateMessage: 'An attendance record for this staff and session already exists — edit it instead.',
    // `role` is attendance-only: the tab lists trainee bookings and the
    // trainer's own booking side by side, and they are read for different
    // reasons.
    filters: { session: true, staff: true, role: true },
  },
  {
    key: 'absences',
    group: 'training',
    label: 'Absences',
    icon: AlertTriangle,
    title: 'absence record',
    columns: [
      { key: 'session', label: 'Session', render: (r) => r.courseTitle || r.sessionCode || `#${r.sessionId}` },
      { key: 'staff', label: 'Staff', render: (r) => r.staffName || `#${r.staffId}` },
      // Category over detail: the classification is what the
      // dashboard counts, the sentence underneath is why this person picked it.
      {
        key: 'reasonCategory',
        label: 'Reason',
        render: (r) => (
          <div>
            <div>{r.reasonCategory}</div>
            {r.reasonDetail && <div className="text-xs text-slate-500">{r.reasonDetail}</div>}
          </div>
        ),
      },
      // No Evidence Yes/No column — such a flag would say nothing the
      // attachment icon in the Actions column does not already say.
      { key: 'remarks', label: 'Remarks' },
      {
        key: 'source',
        label: 'Source',
        // A row here is either HR's own entry or the product of an approved
        // declaration — an unapproved absence never reaches this table.
        render: (r) => (r.sourceRequestNo ? (
          <RefLink
            to={`/workflow/absence/${r.sourceRequestId}`}
            onClick={(e) => e.stopPropagation()}
            className="text-blue-600 hover:text-blue-800"
          >
            {r.sourceRequestNo}
          </RefLink>
        ) : (
          <span className="text-slate-600">Manual entry</span>
        )),
      },
    ],
    fields: [
      { key: 'sessionId', label: 'Session', type: 'session', required: true, identity: true },
      { key: 'staffId', label: 'Staff', type: 'staff', required: true, identity: true },
      // Managed list — free text would make the dashboard's
      // breakdown a count of phrasings. 'Others' is the catch-all, and it is
      // only useful with the sentence that explains it (the API enforces it
      // too, for the employee form as well as this one).
      {
        key: 'reasonCategory',
        label: 'Reason',
        type: 'lookup',
        lookup: 'ld_absence_reason',
        required: true,
      },
      {
        key: 'reasonDetail',
        label: 'Details',
        type: 'textarea',
        requiredIf: (form) => form.reasonCategory === ABSENCE_REASON_OTHER,
        hint: `In their own words — required when the reason is "${ABSENCE_REASON_OTHER}".`,
      },
      // No "Evidence provided" yes/no — the upload below IS the answer,
      // exactly as on the employee declaration form. `evidence` is derived
      // server-side from the file list.
      { key: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
    attachments: {
      path: 'absence-records',
      label: 'Supporting Evidence',
      noun: 'evidence',
      source: 'ABS',
      rowKey: 'evidenceFiles',
    },
    // Trainees only — the trainer cannot be absent from their own session.
    roster: { sessionField: 'sessionId', staffField: 'staffId' },
    duplicateMessage: 'An absence record for this staff and session already exists — edit it instead.',
    filters: { session: true, staff: true },
  },
  {
    key: 'feedback',
    group: 'training',
    label: 'Feedback',
    icon: MessageSquare,
    title: 'feedback record',
    columns: [
      { key: 'session', label: 'Session', render: (r) => r.courseTitle || r.sessionCode || `#${r.sessionId}` },
      { key: 'staff', label: 'Staff', render: (r) => r.staffName || `#${r.staffId}` },
      { key: 'programEffectiveness', label: 'Program', align: 'right' },
      { key: 'trainerEffectiveness', label: 'Trainer', align: 'right' },
      { key: 'preCompetency', label: 'Pre', align: 'right' },
      { key: 'postCompetency', label: 'Post', align: 'right' },
      { key: 'comments', label: 'Comments' },
    ],
    fields: [
      { key: 'sessionId', label: 'Session', type: 'session', required: true, identity: true },
      { key: 'staffId', label: 'Staff', type: 'staff', required: true, identity: true },
      { key: 'programEffectiveness', label: 'Program Effectiveness', type: 'rating', scale: 'effectiveness' },
      { key: 'trainerEffectiveness', label: 'Trainer Effectiveness', type: 'rating', scale: 'effectiveness' },
      { key: 'preCompetency', label: 'Pre-training Competency', type: 'rating', scale: 'competency' },
      { key: 'postCompetency', label: 'Post-Training Competency', type: 'rating', scale: 'competency' },
      { key: 'comments', label: 'Comments', type: 'textarea' },
    ],
    // Trainees only — feedback is about the trainer, so they are not on the list.
    roster: { sessionField: 'sessionId', staffField: 'staffId' },
    duplicateMessage: 'A feedback record for this staff and session already exists — edit it instead.',
    filters: { session: true, staff: true },
  },
  {
    key: 'cpd',
    // Stays with the training records rather than Staff Development:
    // HR files a CPD certificate in the same sitting as the
    // internal training it complements, and both feed the same hours totals.
    group: 'training',
    // 'External CPD' throughout the UI: in this system CPD
    // means external courses only — internal training is Sessions/Attendance.
    // The tab key, the API :type and the table stay `cpd` (URLs and links in
    // already-sent emails must keep working).
    label: 'External CPD',
    icon: Award,
    title: 'External CPD record',
    columns: [
      { key: 'staff', label: 'Staff', render: (r) => r.staffName || `#${r.staffId}` },
      { key: 'certificateDate', label: 'Certificate Date', render: (r) => formatDateShort(r.certificateDate) },
      { key: 'trainingTitle', label: 'Training Title' },
      // Which shared training category the course files under. '—' = a legacy
      // row without one; the form insists on one when it is next saved.
      {
        key: 'category',
        label: 'Category',
        render: (r) => r.categoryTitle || <span className="text-slate-400">—</span>,
      },
      { key: 'hours', label: 'Hours', align: 'right' },
      // The spot-check flags of the declaration that filed the row (none for
      // a record HR keyed in — HR saw the certificate when they filed it).
      { key: 'flags', label: 'Flags', sortable: false, render: (r) => <CpdFlagChips flags={r.flags || []} /> },
    ],
    // External training is its own bucket:
    // no Matched Session, no mandatory flag — a record is what the certificate
    // says, filed under a category the person picks (never derived).
    fields: [
      { key: 'staffId', label: 'Staff', type: 'staff', required: true, identity: true },
      { key: 'certificateDate', label: 'Certificate Date', type: 'date', required: true },
      { key: 'trainingTitle', label: 'Training Title', type: 'text', required: true },
      { key: 'hours', label: 'Hours', type: 'number', required: true },
      { key: 'categoryId', label: 'Category', type: 'category', required: true },
    ],
    // CPD rows carry staff_id but no session_id, so session filters stay off.
    filters: { staff: true, category: true, flags: true },
    // Drop a PDF certificate on the form and it fills itself in — the same
    // reader the declaration flow uses (docs/modules/workflow-ld-cpd.md).
    certificateAutofill: {
      staffField: 'staffId',
      // The per-certificate fields. Everything NOT listed here (Staff) is
      // shared by every record the upload produces.
      map: {
        trainingTitle: 'trainingTitle',
        certificateDate: 'certificateDate',
        hours: 'hours',
      },
      // Fields the row carries but no reader fills: each certificate
      // picks its own category — two courses in one PDF can file under two —
      // so in batch mode the shared Category field steps aside for these.
      perRowFields: ['categoryId'],
      // One certificate = one record, exactly as one certificate = one
      // declaration on the employee form — a two-course PDF must file two
      // records, not one record plus a nameless attachment.
      multiRecord: true,
    },
    // Where this tab's attachments hang. Separate from certificateAutofill so
    // Qualifications can keep files too without inheriting the CPD reader.
    attachments: {
      path: 'cpd-records', label: 'Certificates', noun: 'certificate',
      source: 'CPD', rowKey: 'certificateFiles',
    },
  },
  {
    key: 'qualifications',
    group: 'development',
    label: 'Qualifications',
    icon: GraduationCap,
    title: 'qualification record',
    // One row per (staff, qualification) — the qualification is picked from
    // the catalogue tab.
    columns: [
      { key: 'staff', label: 'Staff', render: (r) => r.staffName || `#${r.staffId}` },
      { key: 'qualification', label: 'Qualification', render: (r) => r.qualificationTitle || `#${r.qualificationTypeId}` },
      { key: 'awardedDate', label: 'Awarded On', render: (r) => formatDateShort(r.awardedDate) || '—' },
      // Only qualifications marked for renewal on the catalogue carry one.
      { key: 'expiryDate', label: 'Expires On', render: (r) => formatDateShort(r.expiryDate) || '—' },
      // Both read-only: Status from staff.date_left, Last Updated from
      // the row's own updated_at. Neither is on the form.
      { key: 'status', label: 'Status' },
      { key: 'lastUpdated', label: 'Last Updated', render: (r) => formatDateShort(r.lastUpdated) },
    ],
    fields: [
      { key: 'staffId', label: 'Staff', type: 'staff', required: true, identity: true },
      { key: 'qualificationTypeId', label: 'Qualification', type: 'qualificationType', required: true },
      { key: 'awardedDate', label: 'Awarded On', type: 'date', required: true },
      // Shown, and mandatory, exactly for the types the catalogue marks
      // `requiresRenewal` — same rule and same visibility as the
      // Workflow declaration: a qualification that never
      // lapses must not offer an expiry date at all.
      {
        key: 'expiryDate',
        label: 'Expires On',
        type: 'date',
        showIf: qualificationLapses,
        requiredIf: qualificationLapses,
        hint: 'The date the current certificate / membership runs out.',
      },
      { key: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
    duplicateMessage: 'This staff member already holds this qualification — edit that row instead.',
    filters: { staff: true },
    // The certificate lives with the record — copied here when a PQ
    // declaration is approved, or attached by HR on a row keyed in by hand.
    attachments: {
      path: 'qualifications', label: 'Certificates', noun: 'certificate',
      source: 'PQ', rowKey: 'certificateFiles',
    },
    note: (
      <>
        One row per qualification held.
        <InfoTip className="ml-1" text="The list of pickable qualifications lives in L&D Setup → Qualification Types; staff can also declare their own via Workflow → Professional Qualification (approval creates the row here). Firm-sponsored agreements live on the Agreements tab." />
      </>
    ),
  },
  {
    key: 'agreements',
    group: 'development',
    label: 'Agreements',
    icon: FileSignature,
    title: 'training agreement',
    columns: [
      { key: 'staff', label: 'Staff', render: (r) => r.staffName || `#${r.staffId}` },
      { key: 'professionalQualification', label: 'Qualification' },
      { key: 'agreementDate', label: 'Agreement Date', render: (r) => formatDateShort(r.agreementDate) },
      { key: 'contractingEntity', label: 'Contracting Entity' },
      { key: 'sponsorshipAwarded', label: 'Sponsored', render: (r) => yesNo(r.sponsorshipAwarded) },
      { key: 'cappedAt', label: 'Capped At' },
      { key: 'signingPartner', label: 'Signing Partner' },
    ],
    fields: [
      { key: 'staffId', label: 'Staff', type: 'staff', required: true, identity: true },
      { key: 'professionalQualification', label: 'Professional Qualification', type: 'text' },
      { key: 'agreementDate', label: 'Agreement Date', type: 'date' },
      { key: 'contractingEntity', label: 'Contracting Entity', type: 'text' },
      { key: 'sponsorshipAwarded', label: 'Sponsorship Awarded', type: 'checkbox' },
      { key: 'cappedAt', label: 'Capped At ($)', type: 'text' },
      { key: 'signingPartner', label: 'Signing Partner', type: 'text' },
      { key: 'statusOfEmployee', label: 'Status of Employee', type: 'text' },
      { key: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
    filters: { staff: true },
    note: 'Several agreements per staff member are allowed. Only awarded ones show as sponsorship on the dashboard.',
  },
  {
    key: 'trainers',
    group: 'setup',
    label: 'Trainers',
    icon: GraduationCap,
    title: 'trainer',
    // Active is the Actions switch plus the greyed-out row (same as Training
    // Categories), so a Yes/No column would say it a third time.
    columns: [
      { key: 'staff', label: 'Staff', render: (r) => r.staffName || `#${r.staffId}` },
      { key: 'position', label: 'Designation', render: (r) => r.position || '—' },
      { key: 'department', label: 'Department', render: (r) => r.department || '—' },
      // Employment, NOT trainer availability: next to the
      // Actions switch a column reading "Active" would be taken for "can be
      // picked as trainer". This one answers a different question — whether the staff
      // member is still with the firm (staff.date_left) — so it is labelled and
      // worded for that, and only the exception (Resigned) is loud. A resigned
      // person leaves the Sessions picker whatever the switch says.
      {
        key: 'status',
        label: 'Employment',
        render: (r) => (r.status === 'Resigned'
          ? <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-amber-100 text-amber-800">Resigned</span>
          : <span className="text-xs text-slate-500">In service</span>),
      },
      { key: 'remarks', label: 'Remarks' },
    ],
    fields: [
      { key: 'staffId', label: 'Staff', type: 'staff', required: true, identity: true },
      { key: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
    // Retiring a trainer is the everyday alternative to deleting the row, so it
    // is a one-click switch in the Actions column rather than a checkbox buried
    // inside Edit. New trainers start active (column default).
    activeToggle: true,
    duplicateMessage: 'This staff member is already on the trainer list — edit that row instead.',
    filters: { staff: true },
    note: (
      <>
        Only the people on this list can be picked as the Trainer of a training session (Training Records → Sessions).
        <InfoTip className="ml-1" text="Seeded with every Partner. To retire someone, switch them off in the Actions column — they leave the Sessions form but the sessions they already ran are untouched. Employment is the staff master's own status: whoever has left the firm drops off the Sessions picker regardless of the switch." />
      </>
    ),
  },
  {
    key: 'qualificationTypes',
    group: 'setup',
    label: 'Qualification Types',
    icon: GraduationCap,
    title: 'qualification type',
    // The catalogue carries real attributes — `title` is the
    // short display name every picker and PQ snapshot reads, the rest describe
    // it (who awards it, where it applies, whether it counts towards ACRA
    // Public Accountant registration).
    // Slimmed to what HR reads at a glance. Full Name rides under the title
    // instead of taking its own column; Renewal, Sort Order and Remarks stay
    // in the form (they answer nothing you scan a table for), and Active is
    // the Actions switch.
    columns: [
      {
        key: 'title',
        label: 'Qualification',
        // The cell styles itself, so it has to honour the retired look the row
        // gets (the td's muted colour cannot reach through these classes).
        render: (r) => (
          <div className="min-w-0">
            <div className={`font-medium truncate ${r.active === false ? 'text-slate-500' : 'text-slate-800'}`} title={r.title}>
              {r.title}
            </div>
            {r.fullName && (
              <div className={`text-xs truncate ${r.active === false ? 'text-slate-500' : 'text-slate-500'}`} title={r.fullName}>
                {r.fullName}
              </div>
            )}
          </div>
        ),
      },
      { key: 'awardingBody', label: 'Awarding Body' },
      { key: 'jurisdiction', label: 'Jurisdiction' },
      {
        key: 'publicPractice',
        label: 'Public Practice',
        // The one question the QM side asks of a qualification — a badge, so
        // the yes rows are findable without reading every line.
        render: (r) => (r.publicPractice
          ? <span className="px-2 py-0.5 text-xs font-medium rounded-full bg-green-100 text-green-800">ACRA</span>
          : <span className="text-slate-400">—</span>),
      },
      { key: 'holderCount', label: 'Holders', align: 'right' },
    ],
    fields: [
      { key: 'title', label: 'Qualification', type: 'text', required: true, placeholder: 'e.g. CA (Singapore)', hint: 'The short name every picker and record shows.' },
      { key: 'fullName', label: 'Full Name', type: 'text', placeholder: 'e.g. Association of Chartered Certified Accountants', full: true },
      { key: 'awardingBody', label: 'Awarding Body', type: 'text', placeholder: 'e.g. ISCA / Singapore Accountancy Commission' },
      // The shared country list, as a searchable dropdown — a 21-row
      // radio list is the wrong control for this many options.
      { key: 'jurisdiction', label: 'Jurisdiction', type: 'lookupSelect', lookup: 'country', placeholder: 'Select a country…', hint: 'Pick Global for body-agnostic certifications (CFA, CIA…).' },
      { key: 'publicPractice', label: 'Recognised for Public Practice (ACRA)', type: 'checkbox', checkboxLabel: 'Counts towards ACRA Public Accountant registration', full: true },
      { key: 'requiresRenewal', label: 'Annual Renewal', type: 'checkbox', checkboxLabel: 'Needs annual renewal / continued membership', full: true },
      { key: 'sortOrder', label: 'Sort Order', type: 'number', placeholder: '0', hint: 'Order in the pickers — low first.' },
      { key: 'remarks', label: 'Remarks', type: 'textarea', full: true },
    ],
    // Sections for the form — two columns, grouped by the question each
    // group answers.
    formLayout: [
      { title: 'What It Is', fields: ['title', 'fullName'] },
      { title: 'Who Awards It', fields: ['awardingBody', 'jurisdiction'] },
      { title: 'How It Counts', fields: ['publicPractice', 'requiresRenewal', 'sortOrder'] },
      { title: 'Notes', fields: ['remarks'] },
    ],
    activeToggle: true,
    duplicateMessage: 'This qualification title is already on the list.',
    note: (
      <>
        The professional qualification catalogue Staff Development → Qualifications and the Workflow declaration pick from.
        <InfoTip className="ml-1" text="Jurisdiction reads the shared Country list (Settings → Dropdown Lists). Sort Order drives the pickers (low first). To retire one, switch it off in the Actions column — holders keep it. Deleting is only possible while nobody holds it." />
      </>
    ),
  },
  {
    key: 'categories',
    group: 'setup',
    label: 'Training Categories',
    icon: ListChecks,
    title: 'training category',
    // Two columns only: the session count is what the
    // delete dialog asks the server for anyway — it belongs there, at the one
    // moment it decides anything — and Active is the switch in Actions plus
    // the greyed-out row, so a Yes/No column would say it a third time.
    columns: [
      { key: 'title', label: 'Category' },
      { key: 'remarks', label: 'Remarks' },
    ],
    fields: [
      { key: 'title', label: 'Category', type: 'text', required: true },
      { key: 'remarks', label: 'Remarks', type: 'textarea' },
    ],
    // Active is a one-click switch in the Actions column, not a form field
    // — retiring a category is the everyday alternative to
    // deleting one, so it must not be buried inside Edit. New categories start
    // active (the column defaults to true).
    activeToggle: true,
    duplicateMessage: 'This category title is already on the list.',
    note: (
      <>
        Every training session is filed under one of the active categories here — whether its hours count towards the 40-hour target is the session&apos;s own Mandatory flag.
        <InfoTip className="ml-1" text="Mandatory and non-mandatory sessions alike carry a category. Renaming a title renames it on every linked session. To retire one, switch it off in the Actions column — it leaves the Sessions form but linked sessions keep it. Deleting is only possible while nothing references it." />
      </>
    ),
  },
  {
    key: 'venues',
    group: 'setup',
    label: 'Venues',
    icon: MapPin,
    title: 'venue',
    // The coordinates are why this is a table and not a text
    // box on the session: GPS-verified check-in needs one fixed point per
    // venue.
    columns: [
      { key: 'name', label: 'Venue' },
      {
        key: 'latitude',
        label: 'Coordinates',
        // One column, because a latitude without its longitude is not an
        // answer — the form and the API keep the pair together too.
        render: (r) => (r.latitude != null && r.longitude != null
          ? <span className="tabular-nums text-xs">{r.latitude}, {r.longitude}</span>
          : <span className="text-slate-500">Not set</span>),
      },
      {
        key: 'radiusM',
        label: 'Radius',
        align: 'right',
        render: (r) => (r.radiusM != null
          ? <span className="tabular-nums">{r.radiusM} m</span>
          : <span className="text-slate-400">—</span>),
      },
      { key: 'remarks', label: 'Remarks' },
    ],
    fields: [
      { key: 'name', label: 'Venue', type: 'text', required: true, placeholder: 'e.g. Taipei Office', full: true },
      // Signed decimals — `min`/`max`/`step` override the default "quantity"
      // number field, which floors at 0 and steps in halves.
      // Required (with the radius): the GPS check-in measures
      // against them — a venue without a point could only answer "cannot judge".
      {
        key: 'latitude',
        label: 'Latitude',
        type: 'number',
        required: true,
        min: -90,
        max: 90,
        step: 'any',
        placeholder: '25.041665',
      },
      {
        key: 'longitude',
        label: 'Longitude',
        type: 'number',
        required: true,
        min: -180,
        max: 180,
        step: 'any',
        placeholder: '121.550128',
      },
      {
        key: 'radiusM',
        label: 'Allowed Radius (m)',
        type: 'number',
        required: true,
        min: 1,
        step: 1,
        newDefault: '100',
        hint: 'How far from the point still counts as being at the venue — the QR check-in page measures each scan against it.',
      },
      { key: 'remarks', label: 'Remarks', type: 'textarea', full: true },
    ],
    formLayout: [
      { title: 'The Venue', fields: ['name'] },
      { title: 'Where It Is', fields: ['latitude', 'longitude', 'radiusM'] },
      { title: 'Notes', fields: ['remarks'] },
    ],
    activeToggle: true,
    duplicateMessage: 'This venue name is already on the list.',
    note: (
      <>
        Where training is held — Training Records → Sessions picks one of the active venues.
        <InfoTip className="ml-1" text="The QR check-in page measures each scan against the coordinates and radius and files an At venue / Outside venue verdict with the attendance. Moving the pin or resizing the radius re-judges sessions that are not yet completed; completed sessions keep the verdicts they froze with. To retire a venue, switch it off in the Actions column — it leaves the Sessions form and the sessions already held there keep it. Deleting is only possible while no session names it." />
      </>
    ),
  },
  // The odd one out: two settings on one form, not a list of records. `panel`
  // says so — the screen renders TrainingSettingsPanel where the table would
  // go and drops the toolbar, the New record button and the paging with it.
  {
    key: 'trainingSettings',
    group: 'setup',
    label: 'Training Settings',
    icon: GraduationCap,
    panel: true,
  },
];

const getTabConfig = (key) => RECORD_TABS.find(t => t.key === key) || RECORD_TABS[0];

// Managed Dropdown Lists (/settings/lists) referenced by `lookup` /
// `lookupSelect` fields — derived from the tab config so adding a field is the
// only change needed. `lookups: []` covers fields that need several lists at
// once (the audience rule editor uses departments and designations).
const LOOKUP_FIELD_CATEGORIES = [...new Set(
  // `panel` tabs carry no fields at all.
  RECORD_TABS.flatMap(t => t.fields || [])
    .flatMap(f => (f.type === 'lookup' || f.type === 'lookupSelect'
      ? [f.lookup]
      : (f.lookups || [])))
)];

const sessionLabel = (s) =>
  `${s.courseTitle || s.sessionCode || `#${s.id}`}${s.date ? ` — ${formatDate(s.date)}` : ''}`;

// `group` picks which of the three screens this is (see TAB_GROUPS) — the
// route passes it; everything else in here works exactly as it did when all ten
// tabs shared one page.
const LDRecords = ({ group = 'training' }) => {
  const { hasPermission } = useAuth();
  const [searchParams, setSearchParams] = useSearchParams();

  const groupConfig = getGroupConfig(group);
  const groupTabs = useMemo(
    () => RECORD_TABS.filter(t => t.group === groupConfig.key),
    [groupConfig.key],
  );

  const rawTab = searchParams.get('tab');
  // A tab from another group never reaches here (LDRecordsPage redirects first),
  // so an unknown ?tab= just falls back to this screen's first tab.
  const activeTab = groupTabs.some(t => t.key === rawTab) ? rawTab : groupTabs[0].key;
  const tabConfig = getTabConfig(activeTab);
  // A settings form rather than a record list — no table, toolbar or paging.
  const isPanel = !!tabConfig.panel;

  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const [search, setSearch] = useState('');
  const [staffOptions, setStaffOptions] = useState([]);
  const [sessionOptions, setSessionOptions] = useState([]);
  // The training-category list — behind the Sessions form's Category
  // picker and the audience-rule editor's category dropdown. Fetched WITH the
  // retired entries so the Sessions list filter can name one (a retired
  // category keeps its sessions); the forms get the active-only view below.
  const [categoryOptions, setCategoryOptions] = useState([]);
  // The qualification catalogue — the Qualifications form.
  const [qualTypeOptions, setQualTypeOptions] = useState([]);
  // Active training venues — the Sessions form's Venue picker.
  const [venueOptions, setVenueOptions] = useState([]);
  // Active quizzes — the Sessions form's Quiz picker.
  const [quizOptions, setQuizOptions] = useState([]);
  // { [category]: string[] } — active options of the managed Dropdown Lists.
  const [lookupOptions, setLookupOptions] = useState({});
  const [sortConfig, setSortConfig] = useState(null);
  // Server-side filters (the list endpoint has always taken these — the page
  // simply never offered them).
  const [filterSessionId, setFilterSessionId] = useState('');
  const [filterStaffId, setFilterStaffId] = useState('');
  // Attendance only — '' | 'participant' | 'trainer'. A trainer row is the one
  // whose staff member IS the session's trainer (their row is booked when the
  // session is marked completed); everyone else attended.
  const [filterRole, setFilterRole] = useState('');
  // Sessions only — '' | 'true' | 'false', straight through to the API's
  // `completed`. Not completed = everything HR has not frozen yet, whether or
  // not check-in happens to be open.
  const [filterCompleted, setFilterCompleted] = useState('');
  // Sessions only — the two halves of the classification, filtered
  // independently: the category a session is filed under, and whether its
  // hours count towards the yearly target ('' | 'true' | 'false').
  const [filterCategoryId, setFilterCategoryId] = useState('');
  const [filterMandatory, setFilterMandatory] = useState('');
  // External CPD only — the spot-check filters: a flag code ('' | 'any' |
  // 'none' | code) and "unconfirmed only". Independent of each other: a code
  // alone lists every declaration carrying it, confirmed or not; with the
  // box ticked the ones somebody already confirmed drop out.
  const [filterFlag, setFilterFlag] = useState('');
  const [filterUnconfirmed, setFilterUnconfirmed] = useState(false);
  // Whether the caller may confirm flags / return declarations (ld:cpd:review,
  // reported by the list endpoint) and the row open in the spot-check modal.
  const [canReviewCpd, setCanReviewCpd] = useState(false);
  const [spotCheck, setSpotCheck] = useState(null);
  const [confirmingPage, setConfirmingPage] = useState(false);
  // What the forms may pick from — a retired category is filterable but never
  // assignable (the Edit form re-adds the record's own, marked "(inactive)").
  const activeCategoryOptions = useMemo(
    () => categoryOptions.filter(c => c.active !== false), [categoryOptions]);

  const requestSort = (key) => {
    setPage(1);
    setSortConfig(prev =>
      prev && prev.key === key
        ? { key, direction: prev.direction === 'asc' ? 'desc' : 'asc' }
        : { key, direction: 'asc' });
  };

  const [editing, setEditing] = useState(null);
  const [showCreate, setShowCreate] = useState(false);
  const [deleting, setDeleting] = useState(null);
  const [checkinSession, setCheckinSession] = useState(null); // sessions tab: QR / check-in modal
  const [viewSession, setViewSession] = useState(null);       // sessions tab: read-only detail modal
  const [filesSession, setFilesSession] = useState(null);     // sessions tab: materials / recording modal
  const [historyRecord, setHistoryRecord] = useState(null);   // per-record History modal
  const [locationRecordId, setLocationRecordId] = useState(null); // GPS evidence behind one check-in
  const [completing, setCompleting] = useState(null);         // sessions tab: Mark as completed
  // Green one-liner above the table: what a save did BEYOND the row itself —
  // hours recalculated across a session, a session frozen. Cleared on the next
  // action; nothing here is an error.
  const [notice, setNotice] = useState(null);
  // What is waiting for HR. Absences and disputes are workflows, so
  // this is their pending-review count and the tab badges link across to those
  // queues rather than carrying a tick-off list of their own.
  const [attention, setAttention] = useState({ disputePending: 0, absencePending: 0 });

  // One key per screen: Training Records / Staff Development /
  // L&D Setup each have their own edit permission.
  const canEdit = hasPermission({
    training: 'ld:training:edit',
    development: 'ld:development:edit',
    setup: 'ld:setup:edit',
  }[group] || 'ld:training:edit');

  const loadAttention = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/ld/attention');
      setAttention({
        disputePending: data.disputePending || 0,
        absencePending: data.absencePending || 0,
      });
    } catch (e) {
      console.error('Error loading L&D attention items:', e);
    }
  }, []);

  // Active on/off straight from the Actions column — the
  // tabs that carry the flag keep it out of the Edit form, because retiring a
  // list entry is the everyday move (and the only way out for an entry that is
  // already referenced and therefore undeletable).
  const [togglingActive, setTogglingActive] = useState(null); // record id in flight
  const toggleActive = async (record) => {
    setTogglingActive(record.id);
    setError(null);
    try {
      await fetchJSON(`/api/ld/records/${activeTab}/${record.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: !record.active }),
      });
      reloadAfterChange();
    } catch (e) {
      setError(e.message);
    } finally {
      setTogglingActive(null);
    }
  };

  const switchTab = (key) => {
    setSearchParams({ tab: key });
    setSearch('');
    setPage(1);
    setSortConfig(null);
    setFilterSessionId('');
    setFilterStaffId('');
    setFilterRole('');
    setFilterCompleted('');
    setFilterCategoryId('');
    setFilterMandatory('');
    setFilterFlag('');
    setFilterUnconfirmed(false);
  };

  // Reset to first page whenever the search, filters or the tab change
  useEffect(() => {
    setPage(1);
  }, [search, activeTab, filterSessionId, filterStaffId, filterRole, filterCompleted,
      filterCategoryId, filterMandatory, filterFlag, filterUnconfirmed]);

  // Active staff for selects
  const loadStaffOptions = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/qm/staff/options?active=true');
      setStaffOptions(data.staff || []);
    } catch (e) {
      console.error('Error loading staff options:', e);
    }
  }, []);

  // Training sessions for selects — the dedicated options endpoint returns the
  // full list (the paged records endpoint caps out at 200).
  const loadSessionOptions = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/ld/sessions/options');
      setSessionOptions(data.data || []);
    } catch (e) {
      console.error('Error loading session options:', e);
    }
  }, []);

  // Training categories for the Sessions form's Category picker and the list
  // filter — retired ones included, sorted out per use below.
  const loadCategoryOptions = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/ld/training-categories/options?includeInactive=true');
      setCategoryOptions(data.data || []);
    } catch (e) {
      console.error('Error loading training category options:', e);
    }
  }, []);

  // Qualification catalogue for the Qualifications form.
  const loadQualTypeOptions = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/ld/qualification-types/options');
      setQualTypeOptions(data.data || []);
    } catch (e) {
      console.error('Error loading qualification type options:', e);
    }
  }, []);

  // Training venues for the Sessions form's Venue picker.
  const loadVenueOptions = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/ld/venues/options');
      setVenueOptions(data.data || []);
    } catch (e) {
      console.error('Error loading venue options:', e);
    }
  }, []);

  // Active quizzes for the Sessions form's Quiz picker.
  const loadQuizOptions = useCallback(async () => {
    try {
      const data = await fetchJSON('/api/ld/quizzes/options');
      setQuizOptions(data.data || []);
    } catch (e) {
      console.error('Error loading quiz options:', e);
    }
  }, []);

  // Managed Dropdown Lists behind the `lookup` fields.
  // Reads are open to any logged-in user, so no extra permission is needed.
  // Each list is fetched independently — one failing category must not blank
  // out the others' dropdowns.
  const loadLookupOptions = useCallback(async () => {
    const results = await Promise.all(LOOKUP_FIELD_CATEGORIES.map(async (category) => {
      try {
        const data = await fetchJSON(`/api/settings/lookups/${category}?active=true`);
        return [category, (data.options || []).map(o => o.value)];
      } catch (e) {
        console.error(`Error loading the ${category} dropdown list:`, e);
        return [category, []];
      }
    }));
    setLookupOptions(Object.fromEntries(results));
  }, []);

  const load = useCallback(async () => {
    // A `panel` tab is a form, not a record type — there is no
    // /api/ld/records/:type behind it to call.
    if (getTabConfig(activeTab).panel) {
      setRows([]); setTotal(0); setLoading(false); setError(null);
      return;
    }
    setLoading(true);
    setError(null);
    try {
      const params = new URLSearchParams();
      if (search.trim()) params.set('search', search.trim());
      if (filterSessionId) params.set('sessionId', filterSessionId);
      if (filterStaffId) params.set('staffId', filterStaffId);
      if (filterRole) params.set('role', filterRole);
      if (filterCompleted) params.set('completed', filterCompleted);
      if (filterCategoryId) params.set('categoryId', filterCategoryId);
      if (filterMandatory) params.set('mandatory', filterMandatory);
      if (filterFlag) params.set('flag', filterFlag);
      if (filterUnconfirmed) params.set('unconfirmed', '1');
      if (sortConfig?.key) { params.set('sort', sortConfig.key); params.set('dir', sortConfig.direction); }
      params.set('page', String(page));
      params.set('pageSize', String(PAGE_SIZE));
      const data = await fetchJSON(`/api/ld/records/${activeTab}?${params}`);
      setRows(data.data || []);
      setTotal(data.total || 0);
      setCanReviewCpd(!!data.canReviewCpd);
    } catch (e) { setError(e.message); }
    finally { setLoading(false); }
  }, [activeTab, search, sortConfig, page, filterSessionId, filterStaffId, filterRole,
      filterCompleted, filterCategoryId, filterMandatory, filterFlag, filterUnconfirmed]);

  useEffect(() => {
    if (!canEdit) return;
    loadStaffOptions();
    loadSessionOptions();
    loadLookupOptions();
    loadCategoryOptions();
    loadQualTypeOptions();
    loadVenueOptions();
    loadQuizOptions();
    loadAttention();
  }, [canEdit, loadStaffOptions, loadSessionOptions, loadLookupOptions,
      loadCategoryOptions, loadQualTypeOptions, loadVenueOptions, loadQuizOptions, loadAttention]);

  useEffect(() => {
    if (!canEdit) return;
    load();
  }, [canEdit, load]);

  // Session list also feeds the selects — refresh it after session edits
  const reloadAfterChange = () => {
    load();
    // Deleting an absence (or fixing attendance) can change the work queue.
    loadAttention();
    if (activeTab === 'sessions') loadSessionOptions();
    // Editing the category list changes the Sessions form's Category picker
    // and the session labels built from it.
    if (activeTab === 'categories') { loadCategoryOptions(); loadSessionOptions(); }
    if (activeTab === 'qualificationTypes') loadQualTypeOptions();
    // Renaming or retiring a venue changes the Sessions form's Venue picker
    // and the name shown on every session held there.
    if (activeTab === 'venues') { loadVenueOptions(); loadSessionOptions(); }
  };

  if (!canEdit) {
    return (
      <div className="p-8 max-w-xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6 text-center">
          <h2 className="text-lg font-semibold text-red-800">Editor access required</h2>
          <p className="text-sm text-red-600 mt-1">You need L&amp;D record editing rights to access this area.</p>
        </div>
      </div>
    );
  }

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  // Header and tab bar are the same on both branches below — a `panel` tab
  // keeps them and swaps only what sits underneath.
  const screenHeader = (
    <>
      <Link to="/manage" className="inline-flex items-center gap-1.5 text-sm text-slate-500 hover:text-slate-700">
        <ArrowLeft className="w-4 h-4" /> Management
      </Link>
      <div className="mt-2 mb-4 flex flex-wrap items-end justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold text-slate-900">{groupConfig.title}</h1>
          <p className="text-sm text-slate-500 mt-1">{groupConfig.description}</p>
        </div>
        {/* Nothing to create on a settings form — it saves in place. */}
        {!isPanel && (
          <div className="flex gap-2">
            {/* No separate Batch add — New record on the
                Attendance tab takes the whole list at once. */}
            <button
              onClick={() => setShowCreate(true)}
              className="inline-flex items-center justify-center gap-2 px-3 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700"
            >
              <Plus className="w-4 h-4" /> New Record
            </button>
          </div>
        )}
      </div>
    </>
  );

  const tabBar = (
    <div className="bg-white border-b border-slate-200 px-4 mb-4 rounded-t-lg border border-b-slate-200">
      <div className="flex gap-6 overflow-x-auto">
        {groupTabs.map(t => {
          const Icon = t.icon;
          return (
            <button
              key={t.key}
              onClick={() => switchTab(t.key)}
              className={`py-3 text-sm font-medium transition-colors flex items-center gap-2 whitespace-nowrap ${activeTab === t.key ? 'text-blue-600 border-b-2 border-blue-600' : 'text-slate-500 border-b-2 border-transparent hover:text-slate-700 hover:border-slate-200'}`}
            >
              <Icon className="w-4 h-4" /> {t.label}
              {t.key === 'attendance' && attention.disputePending > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800" title={`${attention.disputePending} attendance dispute(s) awaiting review`}>
                  {attention.disputePending}
                </span>
              )}
              {t.key === 'absences' && attention.absencePending > 0 && (
                <span className="ml-1 px-1.5 py-0.5 text-xs font-semibold rounded-full bg-amber-100 text-amber-800" title={`${attention.absencePending} absence declaration(s) awaiting review`}>
                  {attention.absencePending}
                </span>
              )}
            </button>
          );
        })}
      </div>
    </div>
  );

  // Training Settings: a form where the record table normally is. None of the
  // toolbar, paging or record modals below apply to it.
  if (isPanel) {
    return (
      <div className="p-4 sm:p-6 max-w-7xl mx-auto">
        {screenHeader}
        {tabBar}
        <TrainingSettingsPanel />
      </div>
    );
  }

  return (
    <div className="p-4 sm:p-6 max-w-7xl mx-auto">
      {screenHeader}
      {tabBar}

      {/* Both queues are workflows — this points at them instead of
          holding a work queue of its own (approval writes the record). */}
      {activeTab === 'attendance' && attention.disputePending > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-semibold text-amber-800">
            {attention.disputePending === 1
              ? '1 attendance dispute awaiting review'
              : `${attention.disputePending} attendance disputes awaiting review`}
          </div>
          <Link
            to="/workflow/dispute?pending=1"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-50 shrink-0"
          >
            Open the Review Queue
          </Link>
        </div>
      )}
      {activeTab === 'absences' && attention.absencePending > 0 && (
        <div className="bg-amber-50 border border-amber-200 rounded-lg p-4 mb-4 flex flex-wrap items-center justify-between gap-3">
          <div className="text-sm font-semibold text-amber-800">
            {attention.absencePending === 1
              ? '1 absence declaration awaiting review'
              : `${attention.absencePending} absence declarations awaiting review`}
          </div>
          <Link
            to="/workflow/absence?pending=1"
            className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded-lg hover:bg-slate-50 shrink-0"
          >
            Open the Review Queue
          </Link>
        </div>
      )}

      {/* Toolbar */}
      <div className="bg-white rounded-lg border border-slate-200 p-3 mb-4">
        <div className="flex flex-wrap items-center gap-3">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
            <input
              type="text"
              value={search}
              onChange={e => setSearch(e.target.value)}
              placeholder="Search…"
              className={`${inputCls} pl-9`}
            />
          </div>
          {tabConfig.filters?.session && (
            <div className="min-w-[220px] flex-1 max-w-xs">
              <SearchSelect
                value={filterSessionId}
                onChange={setFilterSessionId}
                options={sessionOptions.map(s => ({ value: s.id, label: sessionLabel(s) }))}
                placeholder="All sessions"
              />
            </div>
          )}
          {tabConfig.filters?.staff && (
            <div className="min-w-[200px] flex-1 max-w-xs">
              <SearchSelect
                value={filterStaffId}
                onChange={setFilterStaffId}
                options={staffOptions.map(s => ({ value: s.id, label: s.name, group: s.position || 'No designation' }))}
                placeholder="All staff"
              />
            </div>
          )}
          {tabConfig.filters?.role && (
            <div className="min-w-[160px]">
              <select
                value={filterRole}
                onChange={e => setFilterRole(e.target.value)}
                className={inputCls}
                aria-label="Filter by role"
              >
                <option value="">All roles</option>
                <option value="participant">Participants</option>
                <option value="trainer">Trainers</option>
              </select>
            </div>
          )}
          {/* Sessions: the two halves of the classification, filtered
              apart — "all Ethics sessions", "everything non-mandatory", or
              both at once. Retired categories are on this list (marked) even
              though the form no longer offers them: their sessions are still
              in the table and have to be reachable. */}
          {tabConfig.filters?.category && (
            <div className="min-w-[200px] flex-1 max-w-xs">
              <SearchSelect
                value={filterCategoryId}
                onChange={setFilterCategoryId}
                options={categoryOptions.map(c => ({
                  value: c.id,
                  label: c.active === false ? `${c.title} (inactive)` : c.title,
                }))}
                placeholder="All categories"
              />
            </div>
          )}
          {tabConfig.filters?.mandatory && (
            <div className="min-w-[180px]">
              <select
                value={filterMandatory}
                onChange={e => setFilterMandatory(e.target.value)}
                className={inputCls}
                aria-label="Filter by mandatory"
              >
                <option value="">All</option>
                <option value="true">Mandatory</option>
                <option value="false">Non-mandatory</option>
              </select>
            </div>
          )}
          {tabConfig.filters?.flags && (
            <>
              <div className="min-w-[200px]">
                <select
                  value={filterFlag}
                  onChange={e => setFilterFlag(e.target.value)}
                  className={inputCls}
                  aria-label="Filter by spot-check flag"
                >
                  <option value="">All records</option>
                  <option value="any">Any flag</option>
                  {CPD_FLAG_CODES.map((code) => (
                    <option key={code} value={code}>{CPD_FLAG_META[code].label}</option>
                  ))}
                  <option value="none">No flags</option>
                </select>
              </div>
              <label className="inline-flex items-center gap-1.5 text-sm text-slate-600 whitespace-nowrap">
                <input
                  type="checkbox"
                  checked={filterUnconfirmed}
                  onChange={e => setFilterUnconfirmed(e.target.checked)}
                />
                Unconfirmed only
              </label>
            </>
          )}
          {tabConfig.filters?.completion && (
            <div className="min-w-[180px]">
              <select
                value={filterCompleted}
                onChange={e => setFilterCompleted(e.target.value)}
                className={inputCls}
                aria-label="Filter by completion"
              >
                <option value="">All statuses</option>
                <option value="true">Completed</option>
                <option value="false">Not completed</option>
              </select>
            </div>
          )}
          {(search || filterSessionId || filterStaffId || filterRole || filterCompleted
            || filterCategoryId || filterMandatory || filterFlag || filterUnconfirmed) && (
            <button
              onClick={() => {
                setSearch(''); setFilterSessionId(''); setFilterStaffId(''); setFilterRole('');
                setFilterCompleted(''); setFilterCategoryId(''); setFilterMandatory('');
                setFilterFlag(''); setFilterUnconfirmed(false);
              }}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              Clear
            </button>
          )}
          <ResultCount count={total} loading={loading} className="ml-auto" />
          {/* Confirm every unconfirmed flag on this page in one go — the
              "I have looked at all ten" button. Honours the flag filter: with
              a code selected only that flag is confirmed on each row, so a
              Name-mismatch pass does not also wave through an Edited one. */}
          {activeTab === 'cpd' && canReviewCpd
            && rows.some((r) => unconfirmedFlags(r.flags || []).some((f) => !filterFlag || ['any', 'none'].includes(filterFlag) || f.code === filterFlag)) && (
            <button
              type="button"
              disabled={confirmingPage}
              onClick={async () => {
                setConfirmingPage(true);
                setError(null);
                try {
                  const ids = rows
                    .filter((r) => r.requestId && unconfirmedFlags(r.flags || []).length > 0)
                    .map((r) => r.requestId);
                  const codes = filterFlag && !['any', 'none'].includes(filterFlag) ? [filterFlag] : [];
                  await fetchJSON('/api/ld/cpd-requests/flags/confirm', {
                    method: 'POST',
                    body: JSON.stringify({ ids, codes }),
                  });
                  setNotice(codes.length
                    ? `${CPD_FLAG_META[codes[0]].label} confirmed on ${ids.length} record${ids.length === 1 ? '' : 's'}.`
                    : `All flags confirmed on ${ids.length} record${ids.length === 1 ? '' : 's'}.`);
                  reloadAfterChange();
                } catch (e) {
                  setError(e.message);
                } finally {
                  setConfirmingPage(false);
                }
              }}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-800 text-white text-xs font-medium rounded-lg hover:bg-slate-900 disabled:opacity-50"
            >
              <ClipboardCheck className="w-3.5 h-3.5" />
              {confirmingPage ? 'Confirming…' : 'Confirm This Page'}
            </button>
          )}
        </div>
      </div>

      {tabConfig.note && (
        <p className="text-xs text-slate-500 mb-3 -mt-1">{tabConfig.note}</p>
      )}

      {error && (
        <div className="bg-red-50 border border-red-200 rounded-lg p-4 text-red-700 text-sm mb-4">{error}</div>
      )}

      {notice && (
        <div className="bg-green-50 border border-green-200 rounded-lg p-3 text-green-800 text-sm mb-4 flex items-start gap-2">
          <span>{notice}</span>
          <button
            onClick={() => setNotice(null)}
            className="ml-auto shrink-0 text-green-700 hover:text-green-900"
            title="Dismiss"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Table */}
      <div className="bg-white rounded-lg border border-slate-200 overflow-hidden">
        <div className="table-scroll">
          <table className="min-w-full divide-y divide-slate-200 text-sm">
            <thead className="bg-slate-50">
              <tr>
                {tabConfig.columns.map(c => (
                  <SortableHeader key={c.key} label={c.label} sortKey={c.key} align={c.align === 'right' ? 'right' : 'left'} sortConfig={sortConfig} onSort={requestSort} className={THEAD_CLS} />
                ))}
                <SortableHeader label="Actions" sortable={false} align="right" className={THEAD_CLS} />
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={tabConfig.columns.length + 1} className="px-4 py-8 text-center text-slate-500">Loading…</td>
                </tr>
              ) : rows.length === 0 ? (
                <tr>
                  <td colSpan={tabConfig.columns.length + 1} className="px-4 py-8 text-center text-slate-500">No records found</td>
                </tr>
              ) : rows.map(r => {
                // A retired entry reads as retired: the whole row greys out, so
                // the switch is not the only thing saying so.
                const retired = !!tabConfig.activeToggle && r.active === false;
                return (
                <tr key={r.id} className={retired ? 'bg-slate-50/70 hover:bg-slate-100' : 'hover:bg-slate-50'}>
                  {tabConfig.columns.map(c => (
                    <td key={c.key} className={`px-4 py-2 max-w-[280px] truncate ${retired ? 'text-slate-500' : 'text-slate-700'} ${c.align === 'right' ? 'text-right' : ''}`} title={c.render ? undefined : (r[c.key] != null ? String(r[c.key]) : '')}>
                      {/* Second argument: actions a cell may need to open
                          something (the Venue Check badge opens the GPS
                          evidence panel). Columns that only format their own
                          value ignore it. */}
                      {c.render ? c.render(r, { onShowLocation: setLocationRecordId }) : (r[c.key] ?? '—')}
                    </td>
                  ))}
                  <td className="px-4 py-2 text-right">
                    <div className="inline-flex gap-1">
                      {/* The files on this record — copied from an approved
                          declaration, or attached by HR. Certificates for
                          Qualifications and External CPD,
                          evidence for Absences. Lives in the
                          actions rather than its own column: it is something
                          you open, not something
                          you read off the row. Images and PDFs open in a new
                          tab; .doc/.docx download (server/utils/fileResponse.js). */}
                      {(tabConfig.attachments ? r[tabConfig.attachments.rowKey] || [] : []).map((f) => (
                        <a
                          key={f.id}
                          href={`${API_BASE_URL}/api/ld/${tabConfig.attachments.path}/${r.id}/files/${f.id}`}
                          target="_blank"
                          rel="noreferrer"
                          title={f.sourceRequestId
                            ? `Open ${tabConfig.attachments.noun}: ${f.originalName} (from ${tabConfig.attachments.source}-${f.sourceRequestId})`
                            : `Open ${tabConfig.attachments.noun}: ${f.originalName}`}
                          className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                        >
                          <FileBadge className="w-4 h-4" />
                        </a>
                      ))}
                      {/* External CPD filed by a declaration: the spot-check
                          — flags to confirm, the reading against the declared
                          values, and Return. Rows HR keyed in have no
                          declaration and nothing to check. */}
                      {activeTab === 'cpd' && r.requestId && (
                        <button
                          onClick={() => setSpotCheck(r)}
                          title={unconfirmedFlags(r.flags || []).length > 0
                            ? `Spot-check ${r.requestNo} — ${unconfirmedFlags(r.flags || []).length} flag${unconfirmedFlags(r.flags || []).length === 1 ? '' : 's'} to confirm`
                            : `Spot-check ${r.requestNo}`}
                          className={`p-1.5 rounded ${unconfirmedFlags(r.flags || []).length > 0
                            ? 'text-amber-600 hover:text-amber-800 hover:bg-amber-50'
                            : 'text-slate-500 hover:text-blue-600 hover:bg-blue-50'}`}
                        >
                          <ClipboardCheck className="w-4 h-4" />
                        </button>
                      )}
                      {activeTab === 'sessions' && (
                        <>
                          <button
                            onClick={() => setViewSession(r)}
                            title="View attendance, absences and feedback"
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setFilesSession(r)}
                            title="Course materials and recording"
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <FolderOpen className="w-4 h-4" />
                          </button>
                          <button
                            onClick={() => setCheckinSession(r)}
                            title="QR check-in"
                            className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                          >
                            <QrCode className="w-4 h-4" />
                          </button>
                          {/* The one-way door. Hidden once pressed —
                              there is nothing to press twice and no reopen. */}
                          {!isCompletedSession(r) && (
                            <button
                              onClick={() => setCompleting(r)}
                              title="Mark as completed — freezes the session and its attendee list"
                              className="p-1.5 text-slate-500 hover:text-green-700 hover:bg-green-50 rounded"
                            >
                              <CheckCircle2 className="w-4 h-4" />
                            </button>
                          )}
                        </>
                      )}
                      {/* Active on/off lives here, not in Edit: it is the
                          everyday alternative to
                          deleting a list entry. A real switch rather than an
                          icon: toggle
                          glyphs differ only by knob position and at 16px
                          nobody can tell on from off. Filled track — green
                          on, grey off — reads at a glance. */}
                      {tabConfig.activeToggle && (
                        <button
                          type="button"
                          role="switch"
                          aria-checked={!!r.active}
                          aria-label={r.active ? 'Active' : 'Inactive'}
                          onClick={() => toggleActive(r)}
                          disabled={togglingActive === r.id}
                          title={r.active
                            ? 'Active — click to deactivate. It leaves the forms; existing records keep it.'
                            : 'Inactive — click to reactivate. It returns to the forms.'}
                          className="p-1.5 rounded disabled:opacity-40 disabled:cursor-not-allowed align-middle"
                        >
                          <span className={`flex h-5 w-9 items-center rounded-full transition-colors ${
                            r.active ? 'bg-green-500 hover:bg-green-600' : 'bg-slate-300 hover:bg-slate-400'}`}>
                            <span className={`h-4 w-4 rounded-full bg-white shadow transition-transform ${
                              r.active ? 'translate-x-[18px]' : 'translate-x-0.5'}`} />
                          </span>
                        </button>
                      )}
                      <button
                        onClick={() => setHistoryRecord(r)}
                        title="History"
                        className="p-1.5 text-slate-500 hover:text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <History className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => setEditing(r)}
                        disabled={isCompletedSession(r)}
                        title={isCompletedSession(r)
                          ? 'Marked completed — the session record is frozen and cannot be reopened.'
                          : 'Edit'}
                        className={`p-1.5 rounded ${isCompletedSession(r)
                          ? 'text-slate-300 cursor-not-allowed'
                          : 'text-slate-500 hover:text-blue-600 hover:bg-blue-50'}`}
                      >
                        {isCompletedSession(r) ? <Lock className="w-4 h-4" /> : <Pencil className="w-4 h-4" />}
                      </button>
                      <button
                        onClick={() => setDeleting(r)}
                        title="Delete"
                        className="p-1.5 text-slate-500 hover:text-red-600 hover:bg-red-50 rounded"
                      >
                        <Trash2 className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
                );
              })}
            </tbody>
          </table>
        </div>
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t border-slate-200 bg-slate-50 text-sm">
          <span className="text-slate-500">
            {total > 0
              ? `${(page - 1) * PAGE_SIZE + 1}–${Math.min(page * PAGE_SIZE, total)} of ${total}`
              : '0 of 0'}
          </span>
          <div className="flex items-center gap-2">
            <button
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page <= 1}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              <ChevronLeft className="w-4 h-4" /> Prev
            </button>
            <span className="text-slate-500">Page {page} of {totalPages}</span>
            <button
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
              className="inline-flex items-center gap-1 px-2.5 py-1.5 border border-slate-300 rounded-md text-slate-600 hover:bg-white disabled:opacity-40 disabled:cursor-not-allowed"
            >
              Next <ChevronRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </div>

      {spotCheck && (
        <CpdSpotCheckModal
          record={spotCheck}
          canReview={canReviewCpd}
          onClose={() => setSpotCheck(null)}
          onChanged={(text) => { setNotice(text); reloadAfterChange(); }}
        />
      )}
      {editing && (
        <RecordModal
          type={activeTab}
          tabConfig={tabConfig}
          record={editing}
          staffOptions={staffOptions}
          sessionOptions={sessionOptions}
          categoryOptions={activeCategoryOptions}
          qualTypeOptions={qualTypeOptions}
          venueOptions={venueOptions}
          quizOptions={quizOptions}
          lookupOptions={lookupOptions}
          onClose={() => setEditing(null)}
          // A session edit can re-book everyone on it — say so, because
          // the row itself gives no sign that N other rows just changed.
          onSaved={(result) => {
            setEditing(null);
            setNotice(result?.recalculated > 0
              ? `Session saved — ${result.recalculated} attendance record${result.recalculated === 1 ? '' : 's'} re-booked from the new hours/trainers.`
              : null);
            reloadAfterChange();
          }}
        />
      )}
      {showCreate && (
        <RecordModal
          type={activeTab}
          tabConfig={tabConfig}
          record={null}
          staffOptions={staffOptions}
          sessionOptions={sessionOptions}
          categoryOptions={activeCategoryOptions}
          qualTypeOptions={qualTypeOptions}
          venueOptions={venueOptions}
          quizOptions={quizOptions}
          lookupOptions={lookupOptions}
          onClose={() => setShowCreate(false)}
          // A multi-certificate upload files several records in one sitting —
          // the list alone would not say how many, or that one was skipped.
          onSaved={(result) => {
            setShowCreate(false);
            setNotice(result?.notice || null);
            reloadAfterChange();
          }}
        />
      )}
      {viewSession && (
        <SessionDetailModal
          session={viewSession}
          onClose={() => setViewSession(null)}
        />
      )}
      {filesSession && (
        <SessionFilesModal
          session={filesSession}
          onClose={() => setFilesSession(null)}
        />
      )}
      {completing && (
        <CompleteSessionModal
          session={completing}
          onClose={() => setCompleting(null)}
          onDone={(snapshot) => {
            setCompleting(null);
            setNotice(`“${completing.courseTitle}” is marked completed — the session and its `
              + `attendee list are frozen, and ${snapshot} attendee${snapshot === 1 ? '' : 's'} had `
              + 'their designation and department snapshotted for the reports.');
            reloadAfterChange();
          }}
        />
      )}
      {checkinSession && (
        <CheckinModal
          session={checkinSession}
          staffOptions={staffOptions}
          // Always re-read the list on the way out: opening or closing check-in
          // (or a scan landing while the modal was up) changes what the row's
          // Edit action is allowed to do, and the row in state still holds the
          // flags from before the modal was opened.
          onClose={() => { setCheckinSession(null); load(); }}
        />
      )}
      {historyRecord && (
        <RecordHistoryModal
          type={activeTab}
          tabConfig={tabConfig}
          record={historyRecord}
          onClose={() => setHistoryRecord(null)}
        />
      )}
      {locationRecordId && (
        <AttendanceLocationModal
          recordId={locationRecordId}
          onClose={() => setLocationRecordId(null)}
        />
      )}
      {deleting && (
        <DeleteRecordModal
          type={activeTab}
          tabConfig={tabConfig}
          record={deleting}
          onClose={() => setDeleting(null)}
          onDeleted={() => { setDeleting(null); reloadAfterChange(); }}
        />
      )}
    </div>
  );
};

// Certificate rows nothing further will happen to. `rejected` = the server
// refused it as an exact duplicate — no retry, no "are you sure?", and
// crucially it does not hold up the rest of the upload.
const ROW_DONE = ['created', 'skipped', 'rejected'];

// ── Trainers editor (sessions) ─────────────────────────────────────────────
// Any number of trainers, each with a role and a factor. Rows hold strings
// ({ staffId, role, factor }) like every other form value; buildValue turns
// them into the API's shape. A factor still sitting at its role's default
// follows a role change; a typed one stays.
const TRAINER_ROLE_OPTIONS = [
  { value: 'lead', label: 'Lead Trainer' },
  { value: 'assistant', label: 'Assistant Trainer' },
];
const defaultFactorFor = (role) => (role === 'assistant' ? '0' : '1');

const trainersFromRecord = (record) => (record?.trainers || []).map((t) => ({
  staffId: String(t.staffId),
  role: t.role || 'lead',
  factor: t.factor == null ? defaultFactorFor(t.role) : String(t.factor),
}));

const trainersPayload = (rows) => (rows || [])
  .filter((r) => r.staffId !== '' && r.staffId != null)
  .map((r) => ({
    staffId: Number(r.staffId),
    role: r.role || 'lead',
    factor: r.factor === '' || r.factor == null ? Number(defaultFactorFor(r.role)) : Number(r.factor),
  }));

/** The form-side check the API repeats: a person per row, a factor >= 0, a lead. */
const trainersProblem = (rows) => {
  const filled = (rows || []).filter((r) => r.staffId !== '' && r.staffId != null);
  if ((rows || []).length > filled.length) {
    return 'Pick a trainer for every row, or remove the empty row.';
  }
  const ids = filled.map((r) => String(r.staffId));
  if (new Set(ids).size !== ids.length) return 'The same person is listed as a trainer twice.';
  for (const r of filled) {
    if (r.factor === '' || r.factor == null) continue; // eslint-disable-line no-continue
    const n = Number(r.factor);
    if (!Number.isFinite(n) || n < 0) return 'A trainer factor cannot be less than 0.';
  }
  if (filled.length > 0 && !filled.some((r) => r.role === 'lead')) {
    return 'At least one trainer must be the lead trainer.';
  }
  return null;
};

const TrainersEditor = ({ rows, onChange, options }) => {
  const update = (i, patch) => onChange(rows.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const remove = (i) => onChange(rows.filter((_, j) => j !== i));
  const add = () => {
    const role = rows.some((r) => r.role === 'lead') ? 'assistant' : 'lead';
    onChange([...rows, { staffId: '', role, factor: defaultFactorFor(role) }]);
  };
  const setRole = (i, role) => {
    const row = rows[i];
    const factor = row.factor === defaultFactorFor(row.role) ? defaultFactorFor(role) : row.factor;
    update(i, { role, factor });
  };
  const picked = rows.map((r) => String(r.staffId));
  const hasLead = rows.some((r) => r.role === 'lead' && r.staffId !== '');

  return (
    <div className="space-y-2">
      {rows.length > 0 && (
        <div className="hidden sm:grid grid-cols-[1fr_11rem_6rem_2.5rem] gap-2 text-xs font-medium text-slate-500">
          <span>Trainer</span><span>Role</span><span>Factor</span><span />
        </div>
      )}
      {rows.map((row, i) => {
        // Someone already on another row is not offered again; the row's own
        // pick stays so it can be re-opened.
        const opts = options
          .filter((t) => String(t.id) === String(row.staffId) || !picked.includes(String(t.id)))
          .map((t) => ({
            value: t.id,
            label: `${t.name}${t.onRoster ? '' : ' — off the trainer list'}`,
            group: t.position || 'No designation',
          }));
        return (
          <div key={i} className="grid grid-cols-1 sm:grid-cols-[1fr_11rem_6rem_2.5rem] gap-2 items-center">
            <SearchSelect
              value={row.staffId}
              onChange={(v) => update(i, { staffId: v })}
              options={opts}
              placeholder="Search trainer…"
            />
            <select
              value={row.role}
              onChange={(e) => setRole(i, e.target.value)}
              className={inputCls}
              aria-label="Role"
            >
              {TRAINER_ROLE_OPTIONS.map((o) => <option key={o.value} value={o.value}>{o.label}</option>)}
            </select>
            <input
              type="number"
              min="0"
              step="0.5"
              value={row.factor}
              onChange={(e) => update(i, { factor: e.target.value })}
              className={inputCls}
              placeholder="Factor"
              aria-label="Factor"
            />
            <button
              type="button"
              onClick={() => remove(i)}
              className="p-2 text-slate-400 hover:text-red-600 justify-self-start"
              title="Remove trainer"
            >
              <X className="w-4 h-4" />
            </button>
          </div>
        );
      })}
      <div className="flex flex-wrap items-center gap-3">
        <button
          type="button"
          onClick={add}
          className="inline-flex items-center gap-1 text-sm text-blue-600 hover:underline"
        >
          <Plus className="w-4 h-4" /> Add Trainer
        </button>
        {rows.length > 0 && !hasLead && (
          <span className="text-xs text-amber-600">One trainer must be the lead trainer.</span>
        )}
      </div>
      {options.length === 0 && (
        <p className="text-xs text-amber-600">
          No one on the trainer list yet — add them on the Trainers tab.
        </p>
      )}
    </div>
  );
};

const RecordModal = ({ type, tabConfig, record, staffOptions, sessionOptions,
  categoryOptions = [], qualTypeOptions = [], venueOptions = [], quizOptions = [],
  lookupOptions = {}, onClose, onSaved }) => {
  const isEdit = !!record;

  const initial = {};
  tabConfig.fields.forEach(f => {
    if (f.type === 'checkbox') {
      initial[f.key] = !!record?.[f.key];
    } else if (f.type === 'date') {
      initial[f.key] = toDateInput(record?.[f.key]);
    } else if (f.type === 'trainers') {
      initial[f.key] = trainersFromRecord(record);
    } else if (f.type === 'audience') {
      // An object, not a string — cloned so editing never mutates the row the
      // list is still rendering.
      initial[f.key] = record?.[f.key]
        ? JSON.parse(JSON.stringify(record[f.key]))
        : { include: [], exclude: { staffIds: [] } };
    } else {
      // isMandatory (a boolean) stringifies to 'true'/'false' here, exactly
      // what its radio pair uses; a legacy row's NULL categoryId lands as ''
      // so the required check makes HR classify it on the next save.
      initial[f.key] = record?.[f.key] != null
        ? String(record[f.key])
        : (record ? '' : (f.newDefault ?? ''));
    }
  });

  const [form, setForm] = useState(initial);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  // External CPD soft duplicate guard: the server found existing records for
  // the same person/date — HR confirms via "Create anyway" or backs out.
  const [duplicateSuspect, setDuplicateSuspect] = useState(null);

  const setField = (k, v) => setForm(prev => ({ ...prev, [k]: v }));

  // What `requiredIf` rules get to read beyond the form itself: the catalogue
  // (is the picked qualification one that must be renewed?) and the row being
  // edited, whose own flag rides along for a type since retired.
  const requiredCtx = { qualTypeOptions, record };

  // ── Certificate autofill (External CPD) ────────────────────────────────────
  // Reads an uploaded PDF and fills the form in. The file is never saved — this
  // table keeps no attachments; it saves HR the typing, nothing more.
  const autofillCfg = tabConfig.certificateAutofill || null;
  // This tab's attachment registry — { path, label, noun, … } or null when the
  // tab keeps no files. Certificates for CPD/Qualifications, evidence for
  // Absences; one block serves all three.
  const attachCfg = tabConfig.attachments || null;
  const certPath = attachCfg?.path || null;
  const [certFiles, setCertFiles] = useState([]);

  const applyCertificate = useCallback((r) => {
    if (!autofillCfg) return;
    setForm(prev => {
      const next = { ...prev };
      Object.entries(autofillCfg.map).forEach(([readKey, fieldKey]) => {
        if (r[readKey] != null) next[fieldKey] = String(r[readKey]);
      });
      // Only ever fills an empty staff field — never overrides a person HR
      // already picked, and never on edit, where staff is a locked identity.
      const staffField = autofillCfg.staffField;
      if (staffField && !isEdit && !prev[staffField] && r.staffSuggestion?.staffId) {
        next[staffField] = String(r.staffSuggestion.staffId);
      }
      return next;
    });
  }, [autofillCfg, isEdit]);

  // One record per certificate, on create only. Editing an
  // existing row is still a single record with a single set of fields — a
  // second certificate there has nothing to become.
  const batchCapable = !!autofillCfg?.multiRecord && !isEdit;

  const certAutofill = useCpdAutofill({
    files: certFiles,
    // Only the staff suggestion is taken from the reader here: the course
    // fields are filled row by row below, so that a file holding several
    // certificates does not quietly collapse into the first one.
    onExtracted: (certificates) => { if (certificates[0]) applyCertificate(certificates[0]); },
    disabled: !autofillCfg,
    // Batch mode reads EVERY file picked; the single-record form only ever
    // needed the first.
    multiple: batchCapable,
    // HR files on behalf of others — the name on the certificate is a lookup
    // hint here, not a "this isn't yours" warning (that belongs on the
    // self-service declaration form).
    selfDeclaration: false,
  });

  // ── Certificate rows (create + External CPD only) ───────────────────────────
  // One row per certificate found across the upload; each becomes its own
  // record. Rows drive the course fields from the moment there is at least one,
  // so a single certificate and a PDF holding three behave identically —
  // exactly as on the declaration form.
  const [certRows, setCertRows, certRowsGen] = useCpdRows({
    files: certFiles, autofill: certAutofill, enabled: batchCapable,
  });
  const batchMode = batchCapable && certRows.length > 0;
  // Per-row outcome of the last save run, keyed by row index:
  //   { state: 'created'|'duplicate'|'skipped'|'error', … }
  const [rowResults, setRowResults] = useState({});
  const [attempted, setAttempted] = useState(false);
  // A fresh read is a fresh attempt.
  useEffect(() => { setRowResults({}); setAttempted(false); }, [certRowsGen]);

  const rowFieldKeys = useMemo(
    () => (autofillCfg
      ? [...Object.values(autofillCfg.map), ...(autofillCfg.perRowFields || [])]
      : []),
    [autofillCfg],
  );
  const rowLabels = useMemo(() => {
    const labelOf = (k) => tabConfig.fields.find(f => f.key === k)?.label;
    return {
      trainingTitle: labelOf(autofillCfg?.map?.trainingTitle) || 'Course / Seminar',
      certificateDate: labelOf(autofillCfg?.map?.certificateDate) || 'Certificate Date',
      hours: labelOf(autofillCfg?.map?.hours) || 'Hours',
      categoryId: labelOf('categoryId') || 'Category',
    };
  }, [autofillCfg, tabConfig]);

  const setRowField = (index, key, value) =>
    setCertRows(prev => prev.map((r, i) => (i === index ? { ...r, [key]: value } : r)));

  // Dropping a row drops that certificate from this sitting — and with it the
  // copy of the file that would have been filed against it (a file no row
  // points at is not kept, same rule as the declaration form). A row already
  // saved is not removable; it is a record now.
  const removeRow = (index) => {
    const left = certRows.filter((_, i) => i !== index);
    setCertRows(left);
    // Last one out clears the upload too: with no rows the modal falls back to
    // the file field's own name + remove button, and leaving the picked files
    // behind would show a name with nothing to go with it.
    if (!left.length) setCertFiles([]);
    setRowResults(prev => {
      const next = {};
      Object.entries(prev).forEach(([k, v]) => {
        const at = Number(k);
        if (at < index) next[at] = v;
        else if (at > index) next[at - 1] = v;
      });
      return next;
    });
  };

  // Certificates already filed against this record — uploaded
  // here, or copied over when a declaration was approved.
  const [savedCerts, setSavedCerts] = useState([]);
  const loadSavedCerts = useCallback(() => {
    if (!certPath || !isEdit || !record?.id) return;
    fetchJSON(`/api/ld/${certPath}/${record.id}/files`)
      .then(d => setSavedCerts(d.data || []))
      .catch(() => setSavedCerts([]));
  }, [certPath, isEdit, record?.id]);
  useEffect(loadSavedCerts, [loadSavedCerts]);

  // A record is formal data, so retiring one of its files is two different
  // acts (docs/data-retention.md): normally the file is REPLACED and stays on
  // the record as a previous version; only a mis-upload or personal data is
  // destroyed, and that one has to say why.
  const [purging, setPurging] = useState(null);   // the file awaiting a reason

  const supersedeSavedCert = async (fileId) => {
    try {
      await fetchJSON(`/api/ld/${certPath}/${record.id}/files/${fileId}`, { method: 'DELETE' });
      loadSavedCerts();
    } catch (e) { setError(e.message); }
  };

  const purgeSavedCert = async (fileId, reason) => {
    try {
      await fetchJSON(`/api/ld/${certPath}/${record.id}/files/${fileId}`, {
        method: 'DELETE',
        body: JSON.stringify({ purge: true, reason }),
      });
      setPurging(null);
      loadSavedCerts();
    } catch (e) { setError(e.message); }
  };

  // ── Trainer pickers (sessions) ─────────────────────────────────────────────
  // Only the maintained roster may be booked. `keep` carries the trainers this
  // record already has, so editing an old session whose trainer has since been
  // retired from the roster does not silently blank the row.
  const trainerField = tabConfig.fields.find(f => f.type === 'trainers') || null;
  const keepTrainerIds = trainerField
    ? (record?.[trainerField.key] || []).map((t) => t.staffId).join(',')
    : '';
  const [trainerOptions, setTrainerOptions] = useState([]);

  useEffect(() => {
    if (!trainerField) return undefined;
    let cancelled = false;
    fetchJSON(`/api/ld/trainers/options${keepTrainerIds ? `?keep=${keepTrainerIds}` : ''}`)
      .then(d => { if (!cancelled) setTrainerOptions(d.data || []); })
      .catch(() => { if (!cancelled) setTrainerOptions([]); });
    return () => { cancelled = true; };
  }, [trainerField, keepTrainerIds]);

  // ── Session-scoped staff picking (attendance / absences / feedback) ─────────
  // There is no enrolment table, so the roster endpoint derives who belongs on
  // this session: the bound expected list plus the trainer. Picking the
  // session first is therefore a hard prerequisite for picking a person.
  const rosterCfg = tabConfig.roster || null;
  const rosterSessionId = rosterCfg ? form[rosterCfg.sessionField] : '';
  const [roster, setRoster] = useState(null);          // { session, staff: [] }
  const [rosterLoading, setRosterLoading] = useState(false);
  const [rosterError, setRosterError] = useState(null);

  useEffect(() => {
    // Editing shows session + staff as read-only identity fields, so the roster
    // has nothing left to constrain — don't spend a request on it.
    if (!rosterCfg || isEdit || !rosterSessionId) { setRoster(null); setRosterError(null); return undefined; }
    let cancelled = false;
    setRosterLoading(true);
    setRosterError(null);
    fetchJSON(`/api/ld/sessions/${rosterSessionId}/roster`)
      .then(d => { if (!cancelled) setRoster({ session: d.session, staff: d.data || [] }); })
      .catch(e => { if (!cancelled) { setRoster(null); setRosterError(e.message); } })
      .finally(() => { if (!cancelled) setRosterLoading(false); });
    return () => { cancelled = true; };
  }, [rosterCfg, isEdit, rosterSessionId]);

  // The endpoint already excludes the trainer from every session's list
  // — they are never an attendee, an absentee or a respondent, and
  // their hours arrive automatically when the session is marked completed.
  // On top of that the attendance form drops anyone the session has already
  // settled: they checked in, or an approved absence excused them.
  const rosterOptions = useMemo(() => {
    if (!roster) return [];
    if (!rosterCfg?.excludeSettled || isEdit) return roster.staff;
    return roster.staff.filter(s => !s.attended && !s.absent);
  }, [roster, rosterCfg, isEdit]);

  // How many the filter above is hiding — worth saying out loud, otherwise a
  // short list reads as a broken expected list rather than a finished one.
  const settledCount = useMemo(() => {
    if (!roster || !rosterCfg?.excludeSettled || isEdit) return 0;
    return roster.staff.filter(s => s.attended || s.absent).length;
  }, [roster, rosterCfg, isEdit]);

  // Attendance New takes the whole list at once. Kept out of
  // `form` because it is not a field: it fans out into one record per person.
  const isMultiStaff = !!rosterCfg?.multiCreate && !isEdit;
  const [pickedStaff, setPickedStaff] = useState([]);
  const toggleStaff = (staffId) => {
    setPickedStaff(prev => (prev.some(id => String(id) === String(staffId))
      ? prev.filter(id => String(id) !== String(staffId))
      : [...prev, staffId]));
  };

  // Changing the session invalidates a person picked from the previous roster.
  useEffect(() => {
    if (!rosterCfg || isEdit || !roster) return;
    if (isMultiStaff) {
      setPickedStaff(prev => prev.filter(id => rosterOptions.some(s => String(s.staffId) === String(id))));
      return;
    }
    const picked = form[rosterCfg.staffField];
    if (picked && !rosterOptions.some(s => String(s.staffId) === String(picked))) {
      setField(rosterCfg.staffField, '');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rosterOptions]);

  // Hours follow the session: everyone on this form is an attendee, so it is
  // always the session's duration and no trainer hours. (Trainer hours are not
  // enterable here at all — the trainer is not on the list, and their row is
  // written when the session is marked completed.)
  const hoursCfg = rosterCfg?.hours || null;
  useEffect(() => {
    if (!hoursCfg || isEdit || !roster) return;
    setForm(prev => ({
      ...prev,
      [hoursCfg.training]: roster.session.hours != null
        ? String(roster.session.hours)
        : prev[hoursCfg.training],
      [hoursCfg.trainer]: '0',
    }));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [roster]);

  // Locked = derived from the session and nobody's to change by hand. A session
  // with no hours set stays editable, otherwise a required field would be stuck
  // empty.
  const isLockedHoursField = (f) => {
    if (!hoursCfg || isEdit) return false;
    if (f.key !== hoursCfg.training && f.key !== hoursCfg.trainer) return false;
    if (!roster) return true;                        // no session picked yet
    if (roster.session.hours == null) return false;  // nothing to derive from
    return true;
  };

  // A `showIf` field that does not apply to the current answers: hidden AND
  // saved as null, so switching a qualification to one that never lapses
  // cannot leave yesterday's expiry date on the record.
  const isConditionalHidden = (f) => !!f.showIf && !f.showIf(form, requiredCtx);

  // Trainer Hours is never entered on this form — a locked
  // 0 box only raises the question of what it is for.
  //
  // In batch mode the three certificate fields move INTO the rows (one set per
  // certificate), so leaving a second copy on the main form would be two places
  // to type the same thing, disagreeing with each other.
  const isHiddenField = (f) =>
    (!!hoursCfg && !isEdit && f.key === hoursCfg.trainer)
    || (batchMode && rowFieldKeys.includes(f.key))
    || isConditionalHidden(f);

  const editableFields = tabConfig.fields.filter(f => !(isEdit && f.identity));

  // Editing a session's audience rules: the bound expected list is rebuilt as
  // part of the save, so this drives both the warning in the form and the
  // Save button's wording (rebuilding must not be skippable).
  // Compared key-sorted: the stored copy comes back from jsonb in its own key
  // order, which a plain stringify would read as a change.
  const audienceField = tabConfig.fields.find(f => f.type === 'audience');
  const rulesChanged = useMemo(() => {
    if (!isEdit || type !== 'sessions' || !audienceField) return false;
    return canonJson(audienceRulesPayload(form[audienceField.key]))
      !== canonJson(audienceRulesPayload(record?.audienceRules));
  }, [isEdit, type, audienceField, form, record]);

  // Check-in live = the sitting is settled. Date, time, hours and the expected
  // list stop being editable here; the trainer and the
  // course title stay open, and the QR screen's Regenerate still re-runs the
  // same rules against today's staff data.
  const checkinOpen = isEdit && type === 'sessions' && !!record?.checkinOpen;
  const isCheckinLocked = (f) => checkinOpen && !!f.lockWhileCheckinOpen;

  // Batch mode: once the first certificate has become a record, the fields
  // SHARED by the batch (Staff) are settled — letting them change now would
  // quietly file the remaining certificates against a different person than
  // the ones already saved.
  const isBatchLocked = (f) =>
    batchMode && createdRows.length > 0 && !rowFieldKeys.includes(f.key);

  const buildValue = (f) => {
    const v = form[f.key];
    if (f.type === 'checkbox') return !!v;
    // Rule sets travel as JSON; incomplete rules are dropped rather than
    // rejected by the API (the editor already flags them on screen).
    if (f.type === 'audience') return audienceRulesPayload(v);
    // Trainer rows → the API's [{ staffId, role, factor }]; empty rows dropped.
    if (f.type === 'trainers') return trainersPayload(v);
    // The Yes/No radio pair holds 'true'/'false' — the API wants the boolean.
    if (f.type === 'mandatoryFlag') return v === 'true';
    if (typeof v === 'string' && v.trim() === '') return null;
    if (f.type === 'number' || f.type === 'rating' || f.type === 'staff'
        || f.type === 'session' || f.type === 'venue'
        || f.type === 'quiz'
        || f.type === 'category' || f.type === 'qualificationType') {
      return Number(v);
    }
    if (typeof v === 'string') return v.trim();
    return v;
  };

  // ── Batch save: one record per certificate row ──────────────────────────────
  // Deliberately built on the SAME two endpoints the single-record path uses
  // (POST the record, then POST its file) rather than a new batch endpoint —
  // the duplicate guard, the audit trail and the history event are the ones
  // already in place, per record. What is new is only that HR is asked once
  // per certificate instead of once per upload.
  const createdRows = Object.values(rowResults).filter(r => r.state === 'created');
  const batchRecordIds = new Set(createdRows.map(r => r.recordId));
  const pendingRows = batchMode
    ? certRows.filter((_, i) => !ROW_DONE.includes(rowResults[i]?.state)).length
    : 0;
  const unresolvedDuplicates = batchMode
    && certRows.some((_, i) => rowResults[i]?.state === 'duplicate');

  // Everything still empty, keyed to the control it belongs to — after the
  // first Save the form marks those controls and lists them in the banner.
  // Batch mode: the shared fields plus each certificate row still to be
  // filed. Otherwise the plain required list (`requiredIf` = required only for
  // certain answers elsewhere on the form: Absences' Details once the reason
  // is the 'Others' catch-all, Qualifications' Expires On once the type needs
  // renewing); identity fields count on create only, and when several people
  // are picked at once the tick list stands in for the staff field.
  const gaps = useMemo(() => {
    const ctx = { qualTypeOptions, record };
    const isRequired = (f) => f.required || (f.requiredIf && f.requiredIf(form, ctx));
    const isEmpty = (f) => form[f.key] === '' || form[f.key] === null;
    const gap = (f) => ({ key: f.key, label: f.label });
    if (batchMode) {
      return [
        ...tabConfig.fields
          .filter(f => !rowFieldKeys.includes(f.key) && isRequired(f) && isEmpty(f))
          .map(gap),
        ...cpdRowsGaps(certRows, {
          labels: rowLabels,
          skip: (_, i) => ROW_DONE.includes(rowResults[i]?.state),
        }),
      ];
    }
    const g = tabConfig.fields
      .filter(f => isRequired(f) && !f.identity && isEmpty(f))
      .map(gap);
    if (!isEdit) {
      tabConfig.fields.filter(f => f.required && f.identity).forEach(f => {
        if (isMultiStaff && f.key === rosterCfg.staffField) {
          if (pickedStaff.length === 0) g.push(gap(f));
        } else if (isEmpty(f)) {
          g.push(gap(f));
        }
      });
    }
    return g;
  }, [form, tabConfig, qualTypeOptions, record, batchMode, rowFieldKeys, certRows, rowLabels,
    rowResults, isEdit, isMultiStaff, rosterCfg, pickedStaff]);
  const validation = useFormValidation(gaps);

  // Everything that is NOT read off a certificate (Staff) is shared by every
  // record this upload creates.
  const buildSharedPayload = () => {
    const payload = {};
    tabConfig.fields
      .filter(f => !rowFieldKeys.includes(f.key))
      .forEach(f => { payload[f.key] = isConditionalHidden(f) ? null : buildValue(f); });
    return payload;
  };

  const finishBatch = (results) => {
    const created = Object.values(results).filter(r => r.state === 'created');
    const skipped = Object.values(results).filter(r => r.state === 'skipped');
    const alreadyOnFile = Object.values(results).filter(r => r.state === 'rejected');
    const failedFiles = created.filter(r => r.fileWarning).length;
    // Nothing was filed — there is no list to refresh and nothing to announce.
    if (created.length === 0) { onClose(); return; }
    const parts = [`${created.length} ${tabConfig.title}${created.length === 1 ? '' : 's'} created`];
    if (alreadyOnFile.length > 0) {
      parts.push(`${alreadyOnFile.length} already recorded, not created again`);
    }
    if (skipped.length > 0) parts.push(`${skipped.length} skipped as a duplicate`);
    if (failedFiles > 0) {
      parts.push(`${failedFiles} certificate file${failedFiles === 1 ? '' : 's'} could not be attached`);
    }
    onSaved({ notice: `${parts.join(' — ')}.` });
  };

  const saveCertificates = async () => {
    setAttempted(true);
    if (!validation.validate()) { setError(null); return; }
    // Empty fields are marked above; what is left for this check is the rule
    // no empty box can express (hours must be a positive number).
    const bad = certRows.findIndex((r, i) => !ROW_DONE.includes(rowResults[i]?.state)
      && cpdRowError(r) !== null);
    if (bad !== -1) {
      setError(certRows.length === 1
        ? cpdRowError(certRows[bad])
        : `Certificate ${bad + 1} (${certRows[bad].filename}): ${cpdRowError(certRows[bad])}`);
      return;
    }

    setSaving(true);
    setError(null);
    const shared = buildSharedPayload();
    const results = { ...rowResults };
    for (let i = 0; i < certRows.length; i += 1) {
      const r = certRows[i];
      const prev = results[i];
      if (ROW_DONE.includes(prev?.state)) continue;                 // eslint-disable-line no-continue
      const payload = {
        ...shared,
        [autofillCfg.map.trainingTitle]: r.trainingTitle.trim(),
        [autofillCfg.map.certificateDate]: r.certificateDate,
        [autofillCfg.map.hours]: Number(r.hours),
      };
      // Row-owned but reader-blind fields (the category each certificate
      // files under) — numbers stay numbers so the API's int check holds.
      (autofillCfg.perRowFields || []).forEach((k) => {
        payload[k] = r[k] !== '' && r[k] != null && !Number.isNaN(Number(r[k]))
          ? Number(r[k]) : r[k];
      });
      // Set only by "Create anyway" on a row the server flagged — never
      // blanket-confirmed for the whole upload.
      if (prev?.state === 'confirmed') payload.confirmDuplicate = true;
      try {
        // eslint-disable-next-line no-await-in-loop
        const created = await fetchJSON(`/api/ld/records/${type}`, {
          method: 'POST', body: JSON.stringify(payload),
        });
        const recordId = created.record?.id;
        // Each record gets its OWN copy of the certificate it came from; a file
        // no row points at is not filed at all (same rule as the declaration
        // form). A failed upload must not read as a failed save — the record
        // exists by this point, so it is reported as its own warning.
        let fileWarning = null;
        const file = certFiles[r.fileIndex];
        if (certPath && file && recordId) {
          const fd = new FormData();
          fd.append('files', file);
          try {
            // eslint-disable-next-line no-await-in-loop
            await fetchJSON(`/api/ld/${certPath}/${recordId}/files`, { method: 'POST', body: fd });
          } catch (e) { fileWarning = e.message; }
        }
        results[i] = { state: 'created', recordId, fileWarning };
      } catch (e) {
        if (e.code === 'EXACT_DUPLICATE') {
          // Same person, same day, same course, same hours. Not a question —
          // there is no answer that turns it into a different training, so the
          // row is closed out and the batch carries on.
          results[i] = { state: 'rejected', message: e.message, similar: e.body?.similar || [] };
        } else if (e.code === 'DUPLICATE_SUSPECT') {
          results[i] = { state: 'duplicate', similar: e.body?.similar || [] };
        } else if (e.code === 'DUPLICATE_RECORD') {
          results[i] = {
            state: 'error',
            message: tabConfig.duplicateMessage || 'A duplicate record already exists — edit it instead.',
          };
        } else {
          results[i] = { state: 'error', message: e.message };
        }
      }
      setRowResults({ ...results });
    }
    setSaving(false);

    const outstanding = certRows.some((_, i) => !ROW_DONE.includes(results[i]?.state));
    // Close out only when something was actually filed. If every certificate
    // came back "already recorded", staying open IS the answer — the rows say
    // which ones and why, where a green banner over a refreshed list would not.
    if (!outstanding && Object.values(results).some(r => r.state === 'created')) {
      finishBatch(results);
    }
  };

  const save = async (confirmDuplicate = false) => {
    if (!validation.validate()) { setError(null); return; }
    // Trainer rows: the same checks the API makes, said before the round trip.
    for (const f of tabConfig.fields) {
      if (f.type !== 'trainers') continue; // eslint-disable-line no-continue
      const problem = trainersProblem(form[f.key]);
      if (problem) { setError(problem); return; }
    }
    // Number fields default to "not negative"; a field carrying its own
    // min/max (venue coordinates) is checked against those instead — the
    // API applies the very same bounds.
    for (const f of tabConfig.fields) {
      if (f.type !== 'number' || form[f.key] === '' || form[f.key] === null) continue;  // eslint-disable-line no-continue
      const n = Number(form[f.key]);
      const min = f.min ?? 0;
      if (!Number.isFinite(n) || n < min || (f.max !== undefined && n > f.max)) {
        setError(f.max !== undefined
          ? `${f.label} must be between ${min} and ${f.max}.`
          : `${f.label} cannot be less than ${min}.`);
        return;
      }
    }
    setSaving(true);
    setError(null);
    setDuplicateSuspect(null);
    const payload = {};
    editableFields.forEach(f => {
      payload[f.key] = isConditionalHidden(f) ? null : buildValue(f);
    });

    try {
      let recordId = record?.id;
      // `recalculated` rides back on a session PATCH: how many
      // attendance rows the new hours/factor just re-booked.
      let saveResult = null;
      if (isEdit) {
        saveResult = await fetchJSON(`/api/ld/records/${type}/${record.id}`, {
          method: 'PATCH',
          body: JSON.stringify(payload),
        });
      } else if (isMultiStaff) {
        // One record per person, filed in one transaction. Anyone already on
        // the session is skipped rather than failing the save — the picker
        // hides them, but the list can go stale while the modal is open.
        const trainerKey = rosterCfg.hours?.trainer;
        const batch = await fetchJSON(`/api/ld/records/${type}/batch`, {
          method: 'POST',
          body: JSON.stringify({
            sessionId: Number(form[rosterCfg.sessionField]),
            entries: pickedStaff.map(staffId => ({
              staffId: Number(staffId),
              [rosterCfg.hours.training]: Number(form[rosterCfg.hours.training]),
              ...(trainerKey ? { [trainerKey]: Number(form[trainerKey] || 0) } : {}),
            })),
          }),
        });
        onSaved({
          notice: `${batch.created} attendance record${batch.created === 1 ? '' : 's'} created`
            + `${batch.skipped > 0 ? `, ${batch.skipped} skipped (already recorded)` : ''}.`,
        });
        return;
      } else {
        tabConfig.fields.filter(f => f.identity).forEach(f => { payload[f.key] = buildValue(f); });
        if (confirmDuplicate) payload.confirmDuplicate = true;
        const created = await fetchJSON(`/api/ld/records/${type}`, {
          method: 'POST',
          body: JSON.stringify(payload),
        });
        recordId = created.record?.id;
      }

      // Attachments go up after the record exists (they hang off its id). A
      // failed upload must not read as a failed save — the record IS saved by
      // this point, so the message says exactly that.
      if (certPath && certFiles.length > 0 && recordId) {
        const fd = new FormData();
        certFiles.forEach(f => fd.append('files', f));
        try {
          await fetchJSON(`/api/ld/${certPath}/${recordId}/files`, { method: 'POST', body: fd });
        } catch (e) {
          setSaving(false);
          setError(`The record was saved, but the ${attachCfg.noun} upload failed: ${e.message}`);
          return;
        }
      }
      // Changing the rules and leaving the bound list alone is not on offer:
      // the save rebuilds it in the same breath, so the
      // head count on screen can never disagree with the rules on screen.
      // Only rule-derived rows are replaced — manual additions and anyone who
      // already attended stay put.
      if (rulesChanged) {
        try {
          await fetchJSON(`/api/ld/sessions/${record.id}/roster/regenerate`, { method: 'POST' });
        } catch (e) {
          setSaving(false);
          setError(`The session was saved, but rebuilding the expected list failed: ${e.message}. `
            + 'Use Regenerate in the QR check-in screen to finish it.');
          return;
        }
      }
      onSaved(saveResult);
    } catch (e) {
      if (e.code === 'DUPLICATE_SUSPECT') {
        // Not an error — the server wants a human decision before filing a
        // possible double-count. Panel with "Create anyway" renders below.
        setDuplicateSuspect(e.body?.similar || []);
      } else if (e.code === 'DUPLICATE_RECORD') {
        setError(tabConfig.duplicateMessage || 'A duplicate record already exists — edit it instead.');
      } else {
        // Every other 409 (check-in open, session completed) already carries the
        // reason — showing the duplicate wording instead would be a lie.
        setError(e.message);
      }
    }
    finally { setSaving(false); }
  };

  const renderField = (f) => {
    // Frozen by an open check-in (or by a batch already part-filed) — shown as
    // plain text rather than a disabled control, so nobody spends time trying
    // to change it.
    if (isCheckinLocked(f) || isBatchLocked(f)) {
      let shown = form[f.key];
      if (f.type === 'audience') shown = summariseAudienceRules(form[f.key]);
      else if (f.type === 'date') shown = formatDate(form[f.key]);
      else if (f.type === 'staff') {
        shown = staffOptions.find(s => String(s.id) === String(form[f.key]))?.name || form[f.key];
      }
      return (
        <div className="px-3 py-2.5 border border-slate-200 bg-slate-50 rounded-lg text-sm text-slate-600 flex items-start gap-2">
          <Lock className="w-4 h-4 shrink-0 mt-0.5 text-slate-400" />
          <span className="break-words">{shown || '—'}</span>
        </div>
      );
    }
    const readOnly = isEdit && f.identity;
    if (readOnly) {
      let display = form[f.key];
      if (f.type === 'staff') display = record.staffName || display;
      if (f.type === 'session') display = record.courseTitle || record.sessionCode || display;
      return (
        <LockTip text="Fixed once the record is saved" block>
          <input type="text" value={display ?? ''} disabled className={lockedInputCls} />
        </LockTip>
      );
    }
    if (isLockedHoursField(f)) {
      return (
        <LockTip text={roster ? 'Taken from the session’s hours' : 'Select a session first — the hours come from it'} block>
          <input
            type="number"
            value={form[f.key]}
            disabled
            className={lockedInputCls}
          />
        </LockTip>
      );
    }
    switch (f.type) {
      case 'staff': {
        if (rosterCfg && f.key === rosterCfg.staffField) {
          if (!rosterSessionId) {
            return <input type="text" value="" disabled placeholder="Select a session first" className={`${inputCls} bg-slate-50`} />;
          }
          if (rosterLoading) {
            return <input type="text" value="" disabled placeholder="Loading the session’s staff…" className={`${inputCls} bg-slate-50`} />;
          }
          if (rosterError) {
            return <p className="text-sm text-red-600">{rosterError}</p>;
          }
          if (rosterOptions.length === 0) {
            return (
              <p className="text-sm text-amber-600">
                {settledCount > 0
                  ? `Everyone expected at this session is already accounted for — `
                    + `${settledCount} attended or excused.`
                  : 'No staff for this session — check its expected attendees on the Sessions tab.'}
              </p>
            );
          }
          if (isMultiStaff) {
            return (
              <div>
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-xs text-slate-500">
                    {pickedStaff.length} selected
                    {settledCount > 0 && ` — ${settledCount} not listed (attended or excused)`}
                  </span>
                  <div className="flex gap-3 text-xs">
                    <button
                      type="button"
                      onClick={() => setPickedStaff(rosterOptions.map(s => s.staffId))}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Select All
                    </button>
                    <button
                      type="button"
                      onClick={() => setPickedStaff([])}
                      className="text-blue-600 hover:text-blue-800"
                    >
                      Clear
                    </button>
                  </div>
                </div>
                <div className="border border-slate-300 rounded-lg p-2 bg-slate-50 max-h-64 overflow-y-auto">
                  {rosterOptions.map(s => (
                    <label key={s.staffId} className="flex items-center py-1 px-2 rounded hover:bg-slate-100 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={pickedStaff.some(id => String(id) === String(s.staffId))}
                        onChange={() => toggleStaff(s.staffId)}
                        className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
                      />
                      <span className="ml-2 text-sm text-slate-700">
                        {s.name} ({s.designation || s.department || '—'})
                      </span>
                    </label>
                  ))}
                </div>
              </div>
            );
          }
          return (
            <SearchSelect
              value={form[f.key]}
              onChange={(v) => setField(f.key, v)}
              options={rosterOptions.map(s => ({
                value: s.staffId,
                label: `${s.name} (${s.designation || s.department || '—'})`,
              }))}
              placeholder="Search staff…"
            />
          );
        }
        return (
          <SearchSelect
            value={form[f.key]}
            onChange={(v) => setField(f.key, v)}
            options={staffOptions.map(s => ({ value: s.id, label: s.name, group: s.position || 'No designation' }))}
            placeholder="Search staff…"
          />
        );
      }
      case 'trainers':
        return (
          <TrainersEditor
            rows={form[f.key] || []}
            onChange={(rows) => setField(f.key, rows)}
            options={trainerOptions}
          />
        );
      case 'session':
        return (
          <SearchSelect
            value={form[f.key]}
            onChange={(v) => setField(f.key, v)}
            options={sessionOptions.map(o => ({ value: o.id, label: sessionLabel(o) }))}
            placeholder="Search session…"
          />
        );
      case 'venue': {
        // Optional — the blank row is a real answer, not an unanswered
        // question. A venue since retired stays selectable on the session that
        // already names it, same rule as every other picker here.
        const options = [...venueOptions];
        if (record?.venueId != null
            && !options.some(v => String(v.id) === String(record.venueId))) {
          options.push({
            id: record.venueId,
            name: `${record.venueName || `#${record.venueId}`} (inactive)`,
          });
        }
        return (
          <div>
            <select value={form[f.key]} onChange={e => setField(f.key, e.target.value)} className={inputCls}>
              <option value="">No venue / online</option>
              {options.map(v => (
                <option key={v.id} value={v.id}>
                  {v.name}{v.latitude != null && v.longitude != null ? '' : ' — no coordinates'}
                </option>
              ))}
            </select>
            {options.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">
                No venues yet — add them on the Venues tab (L&amp;D Setup).
              </p>
            )}
          </div>
        );
      }
      case 'quiz': {
        // Optional — "No quiz" is a real answer and the default. A quiz since
        // retired stays selectable on the session that already names it.
        const options = [...quizOptions];
        if (record?.quizId != null
            && !options.some(q => String(q.id) === String(record.quizId))) {
          options.push({
            id: record.quizId,
            title: `${record.quizTitle || `#${record.quizId}`} (inactive)`,
            questionCount: null,
          });
        }
        return (
          <div>
            <select value={form[f.key]} onChange={e => setField(f.key, e.target.value)} className={inputCls}>
              <option value="">Pick a quiz…</option>
              {options.map(q => (
                <option key={q.id} value={q.id}>
                  {q.title}{q.questionCount != null ? ` — ${q.questionCount} question${q.questionCount === 1 ? '' : 's'}` : ''}
                  {q.passMark != null ? ` · pass ${q.passMark}%` : ''}
                </option>
              ))}
            </select>
            {options.length === 0 && (
              <p className="mt-1 text-xs text-slate-500">
                No quizzes in the bank yet — write one under Management → Quiz Bank.
              </p>
            )}
          </div>
        );
      }
      case 'quizMode': {
        // Three answers as radios, labels only (owner 2026-10-05: no
        // explainer lines). The picker for a bank paper unfolds under
        // "From the Quiz Bank".
        const current = form[f.key] || 'none';
        const choices = [
          { value: 'none', label: 'No quiz' },
          { value: 'trainer', label: 'Trainer will write the quiz' },
          { value: 'bank', label: 'From the Quiz Bank' },
        ];
        return (
          <div className="border border-slate-300 rounded-lg divide-y divide-slate-100">
            {choices.map(c => (
              <label key={c.value} className="flex items-center gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">
                <input
                  type="radio"
                  name={f.key}
                  checked={current === c.value}
                  onChange={() => setField(f.key, c.value)}
                  className="h-4 w-4 shrink-0 text-blue-600 focus:ring-blue-500 border-slate-300"
                />
                <span>{c.label}</span>
              </label>
            ))}
          </div>
        );
      }
      case 'mandatoryFlag': {
        // Mandatory or not is its own question — answered first,
        // explicitly (no default), because it alone decides whether the hours
        // count towards the yearly target.
        const current = form[f.key];
        const choices = [
          { value: 'true', label: 'Mandatory', detail: 'Counts towards the yearly mandatory-hours target' },
          { value: 'false', label: 'Non-mandatory', detail: 'Recorded and reported, but outside the target' },
        ];
        return (
          <div className="border border-slate-300 rounded-lg divide-y divide-slate-100">
            {choices.map(c => (
              <label key={c.value} className="flex items-start gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">
                <input
                  type="radio"
                  name={f.key}
                  checked={current === c.value}
                  onChange={() => setField(f.key, c.value)}
                  className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 focus:ring-blue-500 border-slate-300"
                />
                <span>
                  {c.label}
                  <span className="block text-xs text-slate-500">{c.detail}</span>
                </span>
              </label>
            ))}
          </div>
        );
      }
      case 'category': {
        // The shared category list — required for every session, mandatory or
        // not, so there is no "none" row. A radio list rather
        // than a <select>: titles are long, same reasoning as the lookup
        // fields below.
        const current = form[f.key];
        const options = [...categoryOptions];
        // Editing a session whose category has since been deactivated: keep it
        // selectable instead of silently dropping the link.
        if (record?.categoryId != null
            && !options.some(m => String(m.id) === String(record.categoryId))) {
          options.push({
            id: record.categoryId,
            title: `${record.categoryTitle || `#${record.categoryId}`} (inactive)`,
          });
        }
        return (
          <div className="border border-slate-300 rounded-lg divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {options.map(m => (
              <label key={m.id} className="flex items-start gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">
                <input
                  type="radio"
                  name={f.key}
                  checked={String(current) === String(m.id)}
                  onChange={() => setField(f.key, String(m.id))}
                  className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 focus:ring-blue-500 border-slate-300"
                />
                <span>{m.title}</span>
              </label>
            ))}
            {options.length === 0 && (
              <p className="px-3 py-2 text-xs text-amber-600">
                No categories yet — add them on the Training Categories tab (L&amp;D Setup).
              </p>
            )}
          </div>
        );
      }
      case 'qualificationType': {
        // The catalogue (Qualification Types tab). Editing a record whose
        // qualification has since been deactivated keeps it selectable.
        const options = [...qualTypeOptions];
        if (record?.qualificationTypeId != null
            && !options.some(q => String(q.id) === String(record.qualificationTypeId))) {
          options.push({
            id: record.qualificationTypeId,
            title: `${record.qualificationTitle || `#${record.qualificationTypeId}`} (inactive)`,
          });
        }
        return (
          <div>
            <select value={form[f.key]} onChange={e => setField(f.key, e.target.value)} className={inputCls}>
              <option value="">Select…</option>
              {options.map(q => (
                <option key={q.id} value={q.id}>{q.fullName ? `${q.title} — ${q.fullName}` : q.title}</option>
              ))}
            </select>
            {options.length === 0 && (
              <p className="mt-1 text-xs text-amber-600">
                No qualifications yet — add them on the Qualification Types tab first.
              </p>
            )}
          </div>
        );
      }
      case 'rating': {
        // 1–5 scale as buttons (same control the employee sees on /feedback/:id).
        // Scores are optional here, so clicking the current value clears it.
        const scale = RATING_SCALES[f.scale] || RATING_SCALES.effectiveness;
        const current = form[f.key] === '' || form[f.key] == null ? null : Number(form[f.key]);
        return (
          <div>
            <div className="flex items-center gap-1.5">
              {RATING_OPTIONS.map(n => (
                <button
                  key={n}
                  type="button"
                  aria-pressed={current === n}
                  onClick={() => setField(f.key, current === n ? '' : String(n))}
                  className={`w-10 h-10 rounded-lg border text-sm font-semibold transition-colors ${
                    current === n
                      ? 'bg-blue-600 border-blue-600 text-white'
                      : 'bg-white border-slate-300 text-slate-600 hover:border-blue-400'
                  }`}
                >
                  {n}
                </button>
              ))}
              <span className={`ml-2 text-xs ${current ? 'text-slate-700 font-medium' : 'text-slate-500'}`}>
                {current ? scale.labels[current] : 'Not rated'}
              </span>
            </div>
            <div className="flex justify-between text-[11px] text-slate-500 mt-1 w-[224px]">
              <span>{scale.low}</span>
              <span>{scale.high}</span>
            </div>
          </div>
        );
      }
      case 'select':
        return (
          <select value={form[f.key]} onChange={e => setField(f.key, e.target.value)} className={inputCls}>
            <option value="">{f.placeholder || 'Automatic'}</option>
            {(f.options || []).map(o => <option key={o} value={o}>{o}</option>)}
          </select>
        );
      case 'lookup': {
        const options = lookupOptions[f.lookup] || [];
        const current = form[f.key];
        // A record saved before this list existed (or against an option since
        // deactivated) keeps its value instead of silently resetting to blank.
        const orphan = current && !options.includes(current);
        const all = orphan ? [...options, current] : options;
        // A radio list rather than a <select>: these values are full sentences
        // and a native dropdown clips them to the control's width, in the popup
        // as well as the closed state. Rows wrap, so nothing is hidden. Matches
        // the checkbox group the multilookup fields use.
        //
        // No blank row — the list carries its own catch-all
        // ('Other'). Clicking the selected row still clears it: onChange does
        // not fire for an already-checked radio, so onClick sees the unchanged
        // value and knows this was a click on the current choice.
        return (
          <div className="border border-slate-300 rounded-lg divide-y divide-slate-100 max-h-72 overflow-y-auto">
            {all.map(o => (
              <label key={o} className="flex items-start gap-2 px-3 py-2 text-sm text-slate-700 hover:bg-slate-50 cursor-pointer">
                <input
                  type="radio"
                  name={f.key}
                  checked={current === o}
                  onChange={() => setField(f.key, o)}
                  onClick={() => { if (current === o) setField(f.key, ''); }}
                  className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 focus:ring-blue-500 border-slate-300"
                />
                <span>{o}{orphan && o === current ? ' (not on the list)' : ''}</span>
              </label>
            ))}
            {all.length === 0 && (
              <p className="px-3 py-2 text-xs text-slate-500">No options — add them under Dropdown Lists.</p>
            )}
          </div>
        );
      }
      // Same managed lists as `lookup`, but a searchable dropdown instead of a
      // radio list — for a list of countries the radio column would be
      // 21 rows of scrolling. Short single-word values, so nothing clips.
      case 'lookupSelect': {
        const options = lookupOptions[f.lookup] || [];
        const current = form[f.key];
        // Same orphan rule as `lookup`: a value since removed from the list
        // stays selectable rather than silently blanking on save.
        const all = current && !options.includes(current) ? [...options, current] : options;
        if (all.length === 0) {
          return (
            <input type="text" value="" disabled placeholder="No options — add them under Dropdown Lists." className={`${inputCls} bg-slate-50`} />
          );
        }
        return (
          <SearchSelect
            value={current}
            onChange={(v) => setField(f.key, v)}
            options={all.map(o => ({ value: o, label: o }))}
            placeholder={f.placeholder || 'Select…'}
          />
        );
      }
      case 'audience':
        return (
          <AudienceRulesEditor
            value={form[f.key]}
            onChange={(v) => setField(f.key, v)}
            lookupOptions={lookupOptions}
            staffOptions={staffOptions}
            categoryOptions={categoryOptions}
            qualTypeOptions={qualTypeOptions}
            sessionDate={form.date || null}
            // Editing a session: it must not turn up in its own "missed a past
            // session" picker.
            sessionId={record?.id || null}
            // A trainer is never expected to attend their own session, so the
            // preview drops them exactly as the save does.
            trainerStaffIds={(form.trainers || []).map((t) => Number(t.staffId)).filter((v) => v > 0)}
            fetchJSON={fetchJSON}
          />
        );
      case 'timerange': {
        const raw = form[f.key];
        const { start, end } = parseTimeRange(raw);
        // Both halves picked → the derived field (Hours) is rewritten in the
        // same update, so the two never disagree after a time change. Typing
        // over Hours afterwards still stands until the times move again.
        const setHalf = (which, v) => {
          const next = which === 'start' ? { start: v, end } : { start, end: v };
          const derived = f.derives ? durationHours(next.start, next.end) : '';
          setForm(prev => ({
            ...prev,
            [f.key]: formatTimeRange(next.start, next.end),
            ...(derived !== '' ? { [f.derives]: derived } : {}),
          }));
        };
        return (
          <div>
            <div className="flex items-center gap-2">
              <input
                type="time"
                value={start}
                onChange={e => setHalf('start', e.target.value)}
                className={inputCls}
                aria-label={`${f.label} — start`}
              />
              <span className="text-slate-500 text-sm shrink-0">to</span>
              <input
                type="time"
                value={end}
                onChange={e => setHalf('end', e.target.value)}
                className={inputCls}
                aria-label={`${f.label} — end`}
              />
            </div>
            {/* Free-text values from before the pickers existed cannot be shown
                in a time input — say so rather than blanking them silently. */}
            {raw && !start && !end && (
              <p className="text-xs text-amber-600 mt-1">
                Current value “{raw}” is not a recognised time — picking times will replace it.
              </p>
            )}
            {f.derives && start && end && durationHours(start, end) === '' && (
              <p className="text-xs text-amber-600 mt-1">
                The end time is not after the start time — Hours was left as it is.
              </p>
            )}
          </div>
        );
      }
      case 'number':
        // Hours and the like are quantities in half-steps, so that stays the
        // default. `min`/`max`/`step` are for the fields where it is wrong —
        // venue coordinates run negative and need free decimals.
        return (
          <input
            type="number"
            min={f.min ?? 0}
            {...(f.max !== undefined ? { max: f.max } : {})}
            step={f.step ?? 0.5}
            placeholder={f.placeholder || ''}
            value={form[f.key]}
            onChange={e => setField(f.key, e.target.value)}
            className={inputCls}
          />
        );
      case 'date':
        return (
          <input type="date" value={form[f.key]} onChange={e => setField(f.key, e.target.value)} className={inputCls} />
        );
      case 'checkbox':
        // `checkboxLabel` turns the control into a self-describing row (the
        // question is the label, so no separate field label is needed above
        // it); without it, the original bare Yes tick is unchanged.
        if (f.checkboxLabel) {
          return (
            <label className={`flex items-start gap-2.5 px-3 py-2.5 border rounded-lg cursor-pointer transition-colors ${
              form[f.key] ? 'border-blue-300 bg-blue-50/60' : 'border-slate-300 hover:bg-slate-50'}`}>
              <input
                type="checkbox"
                checked={!!form[f.key]}
                onChange={e => setField(f.key, e.target.checked)}
                className="mt-0.5 h-4 w-4 shrink-0 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
              />
              <span className="text-sm text-slate-700">{f.checkboxLabel}</span>
            </label>
          );
        }
        return (
          <label className="inline-flex items-center gap-2 py-2 cursor-pointer">
            <input
              type="checkbox"
              checked={!!form[f.key]}
              onChange={e => setField(f.key, e.target.checked)}
              className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-slate-300 rounded"
            />
            <span className="text-sm text-slate-700">Yes</span>
          </label>
        );
      case 'textarea':
        return (
          <textarea rows="2" value={form[f.key]} onChange={e => setField(f.key, e.target.value)} className={inputCls} />
        );
      default:
        return (
          <input type="text" value={form[f.key]} onChange={e => setField(f.key, e.target.value)} placeholder={f.placeholder || ''} className={inputCls} />
        );
    }
  };

  const fieldHint = (f) => {
    if (isCheckinLocked(f)) {
      return 'Locked while check-in is open — close check-in in the QR screen to change it.';
    }
    if (isBatchLocked(f)) {
      return 'Locked — records from this upload have already been created for this person.';
    }
    if (isLockedHoursField(f)) {
      if (!roster) return 'Filled in from the session you pick.';
      return `Taken from the session (${roster.session.hours} h).`;
    }
    return f.hint;
  };

  // One field, label and hint included. A self-describing checkbox
  // (`checkboxLabel`) carries its own wording, so it skips the label row.
  const fieldBlock = (f) => (
    f.type === 'checkbox' && f.checkboxLabel
      ? <div key={f.key}>{renderField(f)}{fieldHint(f) && <p className="text-xs text-slate-500 mt-1">{fieldHint(f)}</p>}</div>
      : (
        <FieldRow
          key={f.key}
          field={f.key}
          label={`${f.label}${f.required || (f.requiredIf && f.requiredIf(form, requiredCtx)) ? ' *' : ''}`}
          hint={fieldHint(f)}
        >
          {renderField(f)}
        </FieldRow>
      )
  );

  // `formLayout` (opt-in per tab) groups the fields into titled two-column
  // sections instead of one long stack; `full: true` spans both columns.
  // Anything the layout forgot is appended, so a new field can never vanish
  // from the form just because nobody listed it.
  const visibleFields = tabConfig.fields.filter(f => !isHiddenField(f));
  // The record noun reads lower-case inside sentences; the modal title wants it title-cased.
  const modalNoun = tabConfig.title.split(' ').map((w) => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
  const layoutSections = (() => {
    if (!tabConfig.formLayout) return null;
    const byKey = new Map(visibleFields.map(f => [f.key, f]));
    const sections = tabConfig.formLayout.map(s => ({
      title: s.title,
      fields: s.fields.map(k => byKey.get(k)).filter(Boolean),
    })).filter(s => s.fields.length > 0);
    const listed = new Set(tabConfig.formLayout.flatMap(s => s.fields));
    const rest = visibleFields.filter(f => !listed.has(f.key));
    if (rest.length > 0) sections.push({ title: 'Other', fields: rest });
    return sections;
  })();

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      {/* The Sessions form carries the audience rule editor and its preview —
          the standard column width would squeeze both; a sectioned form needs
          the room for its two columns. */}
      <div className={`bg-white rounded-lg shadow-xl w-full max-h-[90vh] overflow-y-auto ${
        tabConfig.fields.some(f => f.type === 'audience') || layoutSections
        // A stack of certificate cards needs the same room a sectioned form does.
        || certRows.length > 1 ? 'max-w-2xl' : 'max-w-md'}`}>
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            {isEdit ? `Edit ${modalNoun}` : `New ${modalNoun}`}
            {certRows.length > 1 && (
              <span className="ml-2 text-sm font-normal text-slate-500">
                — {certRows.length} certificates, {certRows.length} records
              </span>
            )}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <FormValidationProvider value={validation.providerValue}>
            {checkinOpen && (
              <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700">
                <p className="font-medium">
                  Check-in is open — the sitting itself is locked
                </p>
                <p className="mt-1 text-xs text-slate-500">
                  Date, start/end time, hours and the expected attendees stay as they are while people
                  are scanning in; close check-in in the QR screen to change them.
                  <InfoTip className="ml-1" text="The course and the trainers are still editable, and Regenerate in the QR screen still re-runs the same rules against today's staff data." />
                </p>
              </div>
            )}
            {certPath && (
              <div className="pb-4 border-b border-slate-200">
                <div className="flex items-center justify-between mb-1.5">
                  <label className="block text-sm font-medium text-slate-700">{attachCfg.label}</label>
                  {/* The reader is a CPD-only affordance; the file list itself
                      belongs to any tab that keeps attachments. */}
                  {autofillCfg && certAutofill.button}
                </div>

                <FileList
                  files={savedCerts.filter(f => !f.superseded)}
                  divided={false}
                  className="mb-2 space-y-1"
                  row={(f) => ({
                    name: f.originalName,
                    href: `${API_BASE_URL}/api/ld/${certPath}/${record.id}/files/${f.id}`,
                    icon: null,
                    pad: 'px-2 py-1.5',
                    className: 'bg-slate-50 border border-slate-200 rounded',
                    // Copied from an approved declaration rather than filed
                    // here — worth saying, since replacing it here does not
                    // touch the declaration's own copy.
                    meta: [f.sourceRequestId
                      ? <>from <WorkflowRefLink refNo={`${attachCfg.source}-${f.sourceRequestId}`} /></>
                      : null],
                    actions: (
                      <>
                        <button
                          type="button"
                          onClick={() => supersedeSavedCert(f.id)}
                          className="shrink-0 text-xs text-slate-600 hover:text-slate-800"
                          title="Keep it on the record as a previous version"
                        >
                          Replace
                        </button>
                        <button
                          type="button"
                          onClick={() => setPurging(f)}
                          className="shrink-0 text-xs text-red-600 hover:text-red-800"
                          title="Destroy it — for a mis-upload or personal data only"
                        >
                          Delete Permanently
                        </button>
                      </>
                    ),
                  })}
                />

                {/* Previous versions stay readable but out of the way — this is
                    what "a record keeps its history" looks like on screen. */}
                <PreviousVersions count={savedCerts.filter(f => f.superseded).length}>
                  <FileList
                    files={savedCerts.filter(f => f.superseded)}
                    divided={false}
                    className="mt-1 space-y-1"
                    row={(f) => ({
                      name: f.originalName,
                      href: `${API_BASE_URL}/api/ld/${certPath}/${record.id}/files/${f.id}`,
                      icon: null,
                      muted: true,
                      pad: 'px-2 py-1.5',
                      className: 'border border-dashed border-slate-200 rounded',
                      meta: [`replaced${f.supersededByRef && f.supersededByRef !== 'manual' ? ` by ${f.supersededByRef}` : ''}`],
                      actions: (
                        <button
                          type="button"
                          onClick={() => setPurging(f)}
                          className="shrink-0 text-xs text-red-600 hover:text-red-800"
                        >
                          Delete Permanently
                        </button>
                      ),
                    })}
                  />
                </PreviousVersions>

                {purging && (
                  <PurgeFileModal
                    file={purging}
                    onClose={() => setPurging(null)}
                    onConfirm={(reason) => purgeSavedCert(purging.id, reason)}
                  />
                )}

                <FilePicker
                  multiple
                  kind="docsAndImages"
                  disabled={certAutofill.busy}
                  onChange={e => setCertFiles(Array.from(e.target.files || []))}
                  showSelection={!batchMode}
                  files={certFiles}
                />
                <p className="text-xs text-slate-500 mt-1">
                  {autofillCfg
                    ? `PDFs and photos are read automatically and fill in the fields below (check them
                       before saving). ${batchCapable
                         ? 'One file may hold several certificates — each one becomes its own record, with its own copy of the file. '
                         : ''}Anything the reader cannot make out is simply typed in.
                       Up to ${MAX_CERTIFICATE_PAGES} pages per file.`
                    : `Kept against this record. PDF / JPG / PNG / DOC; images and PDFs open in a new tab from the ${attachCfg.noun} icon in the Actions column.`}
                </p>
                {autofillCfg && certAutofill.panel}
              </div>
            )}
            {layoutSections
              ? layoutSections.map(s => (
                <div key={s.title} className="pt-1 first:pt-0">
                  <h4 className={`${subsectionTitle} mb-2.5`}>
                    <span className={subsectionTitleBar} aria-hidden="true" />
                    {s.title}
                  </h4>
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-4">
                    {s.fields.map(f => (
                      <div key={f.key} className={f.full ? 'sm:col-span-2' : ''}>
                        {fieldBlock(f)}
                      </div>
                    ))}
                  </div>
                </div>
              ))
              : visibleFields.map(f => fieldBlock(f))}
            {/* One card per certificate the reader found. Sits after the shared
                fields (Staff) because that is the reading order: who it is for,
                then what they attended. */}
            {batchMode && (
              <div>
                {certRows.length > 1 && (
                  <div className="flex items-baseline justify-between gap-2 mb-2.5">
                    <h4 className={subsectionTitle}>
                      <span className={subsectionTitleBar} aria-hidden="true" />
                      Certificates — One Record Each
                    </h4>
                    <span className="text-xs text-slate-500">{describeCpdRows(certRows)}</span>
                  </div>
                )}
                <CpdCertificateRows
                  rows={certRows}
                  onChange={setRowField}
                  onRemove={removeRow}
                  attempted={attempted}
                  labels={rowLabels}
                  categoryOptions={categoryOptions}
                  // A row that has become a record is frozen — editing it now
                  // would change nothing (use the row's own Edit action instead).
                  disabled={(r, i) => saving || ROW_DONE.includes(rowResults[i]?.state)}
                  statusOf={(r, i) => {
                    const chip = {
                      created: ['Saved', 'bg-green-50 text-green-700'],
                      skipped: ['Skipped', 'bg-slate-100 text-slate-500'],
                      rejected: ['Already recorded', 'bg-slate-100 text-slate-500'],
                      duplicate: ['Possible duplicate', 'bg-amber-50 text-amber-700'],
                      confirmed: ['Will be created', 'bg-blue-50 text-blue-700'],
                      error: ['Not saved', 'bg-red-50 text-red-700'],
                    }[rowResults[i]?.state];
                    return chip
                      ? <span className={`px-2 py-0.5 rounded text-xs font-medium ${chip[1]}`}>{chip[0]}</span>
                      : null;
                  }}
                  footerOf={(r, i) => {
                    const res = rowResults[i];
                    if (!res) return null;
                    // Turned away outright: the same course, the same day, the
                    // same hours, the same person. Stated, not asked.
                    if (res.state === 'rejected') {
                      return (
                        <div className="mt-3 p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-600">
                          <p className="font-medium text-slate-700">
                            Already recorded — not created again
                          </p>
                          <p className="mt-1 text-xs">{res.message}</p>
                          <ul className="mt-1 text-xs space-y-0.5">
                            {(res.similar || []).map((d) => (
                              <li key={`${d.kind || 'record'}-${d.id}`}>
                                {d.trainingTitle} — {d.hours}h ({formatDate(d.certificateDate)})
                                {d.kind === 'request' ? ` — declaration ${d.requestNo || `#${d.id}`}` : ''}
                                {d.kind !== 'request' && batchRecordIds.has(d.id)
                                  && <span className="font-medium"> — just created from this upload</span>}
                              </li>
                            ))}
                          </ul>
                        </div>
                      );
                    }
                    // The duplicate guard answers per record, so the decision is
                    // asked per row — never once for the whole upload.
                    if (res.state === 'duplicate') {
                      return (
                        <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                          <p className="font-medium">
                            The same course is already on file for this date, with different hours:
                          </p>
                          <ul className="mt-1 text-xs space-y-0.5">
                            {(res.similar || []).map((d) => (
                              <li key={`${d.kind || 'record'}-${d.id}`}>
                                {d.trainingTitle} — {d.hours}h ({formatDate(d.certificateDate)})
                                {d.kind === 'request' ? ` — pending declaration ${d.requestNo || `#${d.id}`}` : ''}
                                {/* Certificates scanned together usually share a
                                    date, so the thing a row most often trips on
                                    is its own sibling from this very upload —
                                    worth saying, or it reads as an old record. */}
                                {d.kind !== 'request' && batchRecordIds.has(d.id)
                                  && <span className="font-medium"> — just created from this upload</span>}
                              </li>
                            ))}
                          </ul>
                          <p className="mt-1 text-xs">Create only if this really is a second sitting rather than the same certificate twice.</p>
                          <div className="mt-2 flex gap-2">
                            <button
                              type="button"
                              onClick={() => setRowResults(p => ({ ...p, [i]: { state: 'confirmed' } }))}
                              className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded hover:bg-amber-700"
                            >
                              Create Anyway
                            </button>
                            <button
                              type="button"
                              onClick={() => setRowResults(p => ({ ...p, [i]: { state: 'skipped' } }))}
                              className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded hover:bg-slate-50"
                            >
                              Skip This One
                            </button>
                          </div>
                        </div>
                      );
                    }
                    if (res.state === 'error') {
                      return <p className="mt-2 text-xs text-red-600">{res.message}</p>;
                    }
                    if (res.state === 'created' && res.fileWarning) {
                      return (
                        <p className="mt-2 text-xs text-amber-700">
                          The record was saved, but its certificate could not be attached: {res.fileWarning}
                        </p>
                      );
                    }
                    return null;
                  }}
                />
              </div>
            )}
            {rulesChanged && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <p className="font-medium">
                  The expected list will be rebuilt when you save
                </p>
                <p className="mt-1 text-xs">
                  Only the people these rules picked are replaced — anyone added by hand and anyone who
                  has already attended stays on the list. Rules that read live data (missed a past
                  session, below the target) are re-evaluated against today’s figures.
                </p>
              </div>
            )}
            {(error || validation.error) && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
                {error || validation.error}
              </div>
            )}
            {duplicateSuspect && (
              <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-800">
                <p className="font-medium">
                  The same course is already on file for this date, with different hours:
                </p>
                <ul className="mt-1 text-xs space-y-0.5">
                  {duplicateSuspect.map((d) => (
                    <li key={`${d.kind || 'record'}-${d.id}`}>
                      {d.trainingTitle} — {d.hours}h ({formatDate(d.certificateDate)})
                      {d.kind === 'request' ? ` — pending declaration ${d.requestNo || `#${d.id}`}` : ''}
                    </li>
                  ))}
                </ul>
                <p className="mt-1 text-xs">Create only if this really is a second sitting rather than the same certificate twice.</p>
                <div className="mt-2 flex gap-2">
                  <button
                    onClick={() => save(true)}
                    disabled={saving}
                    className="px-3 py-1.5 bg-amber-600 text-white text-xs font-medium rounded hover:bg-amber-700 disabled:opacity-50"
                  >
                    Create Anyway
                  </button>
                  <button
                    onClick={() => setDuplicateSuspect(null)}
                    className="px-3 py-1.5 bg-white border border-slate-300 text-slate-700 text-xs font-medium rounded hover:bg-slate-50"
                  >
                    Go Back
                  </button>
                </div>
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              {/* Records already created cannot be un-created by pressing Cancel,
                  so the way out is relabelled once any row has been filed. */}
              <button
                onClick={() => (createdRows.length > 0 ? finishBatch(rowResults) : onClose())}
                className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded"
              >
                {/* Nothing left to do — "Cancel" would imply there is something
                    to call off. */}
                {createdRows.length > 0 || (batchMode && pendingRows === 0) ? 'Close' : 'Cancel'}
              </button>
              {batchMode ? (
                <LockTip text={unresolvedDuplicates ? 'Decide what to do with the flagged certificates first' : null} align="right">
                  <button
                    onClick={saveCertificates}
                    disabled={saving || certAutofill.busy || unresolvedDuplicates || pendingRows === 0}
                    className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                  >
                    {saving
                      ? 'Saving…'
                      : (pendingRows > 1 ? `Save ${pendingRows} Records` : 'Save')}
                  </button>
                </LockTip>
              ) : (
                <button onClick={() => save()} disabled={saving || certAutofill.busy || !!duplicateSuspect} className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50">
                  {saving
                    ? (rulesChanged ? 'Saving & Rebuilding…' : 'Saving…')
                    : (rulesChanged ? 'Save & Rebuild Expected List' : 'Save')}
                </button>
              )}
            </div>
          </FormValidationProvider>
        </div>
      </div>
    </div>
  );
};

// Destroying an attachment on a formal record — the exception path, so the
// dialog's job is to make it feel like one (docs/data-retention.md §1 rule 3).
// The reason is mandatory because it is what survives: the file is gone, and
// the record history keeps "who destroyed what, and why".
const PurgeFileModal = ({ file, onClose, onConfirm }) => {
  const [reason, setReason] = useState('');
  const [busy, setBusy] = useState(false);

  const submit = async () => {
    if (!reason.trim() || busy) return;
    setBusy(true);
    try { await onConfirm(reason.trim()); }
    finally { setBusy(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Delete This File Permanently?</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 space-y-2">
            <p className="font-semibold">
              <span className="break-words">{file.originalName}</span>
            </p>
            <p>
              This destroys the file. Use it only when the document should never have been on
              this record — the wrong client&apos;s document, or personal data that has to go.
            </p>
            <p>
              To replace it with a newer version instead, close this and use <strong>Replace</strong>{' '}
              — the old one stays on the record as a previous version.
            </p>
          </div>
          <div>
            <label className="block text-sm font-medium text-slate-700 mb-1.5">Reason *</label>
            <textarea
              rows="2"
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              className="w-full px-3 py-2.5 border border-slate-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
              placeholder="e.g. wrong client's document attached by mistake"
            />
            <p className="text-xs text-slate-500 mt-1">
              Kept in the record&apos;s history — the file itself will not be.
            </p>
          </div>
        </div>
        <div className="flex justify-end gap-2 p-6 border-t border-slate-200">
          <button
            type="button"
            onClick={onClose}
            className="px-4 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded-lg"
          >
            Cancel
          </button>
          <button
            type="button"
            onClick={submit}
            disabled={busy || !reason.trim()}
            className="px-4 py-2 bg-red-600 text-white text-sm font-medium rounded-lg hover:bg-red-700 disabled:opacity-50"
          >
            {busy ? 'Deleting…' : 'Delete Permanently'}
          </button>
        </div>
      </div>
    </div>
  );
};

const DeleteRecordModal = ({ type, tabConfig, record, onClose, onDeleted }) => {
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState(null);
  // Sessions get an impact preview + explicit cascade confirmation:
  // the dialog fetches what hangs off the session and, when
  // records exist, demands a second tick before sending ?cascade=1. The
  // server enforces the same rule, so a stale preview can never over-delete.
  const isSession = type === 'sessions';
  // A training category that is already referenced can never be deleted — the
  // route refuses it and the FK is RESTRICT. The dialog says so up front and
  // offers the real answer (deactivate) instead of letting the user press
  // Delete and collect a 409.
  const isCategory = type === 'categories';
  // Same story for a venue: sessions point at it with a RESTRICT FK, so
  // the dialog checks first and offers Deactivate rather than a 409.
  const isVenue = type === 'venues';
  const [usage, setUsage] = useState(null);        // sessions/categories/venues only; null = loading
  const [usageError, setUsageError] = useState(false);
  const [ackCascade, setAckCascade] = useState(false);
  const [deactivating, setDeactivating] = useState(false);

  useEffect(() => {
    if (!isSession && !isCategory && !isVenue) return undefined;
    let active = true;
    const url = isSession
      ? `/api/ld/sessions/${record.id}/usage`
      : (isVenue
        ? `/api/ld/venues/${record.id}/usage`
        : `/api/ld/training-categories/${record.id}/usage`);
    fetchJSON(url)
      .then(d => { if (active) setUsage(d.usage); })
      .catch(() => { if (active) { setUsage({}); setUsageError(true); } });
    return () => { active = false; };
  }, [isSession, isCategory, isVenue, record.id]);

  const openDeclarations = (usage?.openAbsenceRequests || 0) + (usage?.openDisputeRequests || 0);
  const hasChildren = isSession && !usageError && usage
    && ((usage.attendance || 0) > 0 || (usage.absences || 0) > 0 || (usage.feedback || 0) > 0
      || openDeclarations > 0);
  const usageLoading = (isSession || isCategory || isVenue) && usage === null;
  // Referenced category: Delete is off for good, whatever the dialog shows.
  // External CPD rows (declarations and records) count too.
  const categoryInUse = isCategory && !usageError && usage
    && ((usage.sessions || 0) > 0 || (usage.audienceRules || 0) > 0
      || (usage.cpdRequests || 0) > 0 || (usage.cpdRecords || 0) > 0);
  const venueInUse = isVenue && !usageError && usage && (usage.sessions || 0) > 0;

  const confirmDelete = async () => {
    setDeleting(true);
    setError(null);
    try {
      await fetchJSON(
        `/api/ld/records/${type}/${record.id}${hasChildren ? '?cascade=1' : ''}`,
        { method: 'DELETE' }
      );
      onDeleted();
    } catch (e) { setError(e.message); }
    finally { setDeleting(false); }
  };

  // The way out for a course that cannot be deleted: retire it. Same PATCH the
  // Actions column's switch sends.
  const deactivate = async () => {
    setDeactivating(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/records/${type}/${record.id}`, {
        method: 'PATCH',
        body: JSON.stringify({ active: false }),
      });
      onDeleted();
    } catch (e) { setError(e.message); }
    finally { setDeactivating(false); }
  };

  // A per-person record (attendance / absence / feedback) is named by the
  // person AND the session — the session alone does not say which row goes.
  const sessionTitle = record.courseTitle || record.trainingTitle || null;
  const subject = (record.staffName && sessionTitle)
    ? `${record.staffName} — ${sessionTitle}`
    : (sessionTitle || record.staffName || record.title || record.name || `#${record.id}`);

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            {categoryInUse ? 'This Category Is in Use'
              : (venueInUse ? 'This Venue Is in Use' : 'Delete Record')}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {categoryInUse ? (
            <p className="text-sm text-slate-700">
              <span className="font-semibold">{subject}</span> is already referenced, so it cannot be
              deleted — the sessions and External CPD rows filed under it would lose their category
              and the figures built from them.
            </p>
          ) : venueInUse ? (
            <p className="text-sm text-slate-700">
              <span className="font-semibold">{subject}</span> is where{' '}
              {usage.sessions} session{usage.sessions === 1 ? ' is' : 's are'} held, so it cannot be
              deleted — those sessions would lose the place they were run at.
            </p>
          ) : (
            <p className="text-sm text-slate-700">
              Are you sure you want to delete the {tabConfig.title} for{' '}
              <span className="font-semibold">{subject}</span>? This action cannot be undone.
            </p>
          )}

          {usageLoading && (
            <p className="text-sm text-slate-500">Checking linked records…</p>
          )}
          {(isSession || isCategory || isVenue) && usageError && (
            <p className="text-sm text-amber-600">
              Could not check the linked records — if any exist, the server will refuse the delete.
            </p>
          )}
          {isVenue && !usageLoading && !usageError && !venueInUse && (
            <p className="text-xs text-slate-500">
              No session is held at this venue, so deleting it changes nothing on the training
              records.
            </p>
          )}
          {venueInUse && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 space-y-2">
              <p className="font-semibold">
                {usage.sessions} training session{usage.sessions === 1 ? '' : 's'} name{usage.sessions === 1 ? 's' : ''} it.
              </p>
              {record.active === false ? (
                <p className="text-xs">
                  It is already deactivated — nobody can pick it on the Sessions form any more, and
                  the sessions above keep it. Leave it on the list as history.
                </p>
              ) : (
                <p className="text-xs">
                  Deactivate it instead: it leaves the Sessions form so nobody books it again, while
                  the sessions already held there are untouched. You can switch it back on at any
                  time from the Actions column.
                </p>
              )}
            </div>
          )}
          {isCategory && !usageLoading && !usageError && !categoryInUse && (
            <p className="text-xs text-slate-500">
              Nothing references this category yet — no session or External CPD row is filed under
              it and no expected list is built from it, so deleting it changes no figures.
            </p>
          )}
          {categoryInUse && (
            <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-sm text-amber-900 space-y-2">
              <p className="font-semibold">
                What still points at it:
              </p>
              <ul className="text-xs space-y-0.5 list-disc pl-5">
                {(usage.sessions || 0) > 0 && (
                  <li>
                    <span className="font-medium">{usage.sessions} training session{usage.sessions === 1 ? '' : 's'}</span>
                    {' '}{usage.sessions === 1 ? 'is' : 'are'} filed under it as their Category —
                    mandatory ones among them feed the 40-hour compliance figures
                  </li>
                )}
                {(usage.audienceRules || 0) > 0 && (
                  <li>
                    <span className="font-medium">{usage.audienceRules} session{usage.audienceRules === 1 ? '' : 's'}</span>
                    {' '}build their expected list from a “Not yet taken” rule naming it
                  </li>
                )}
                {((usage.cpdRequests || 0) + (usage.cpdRecords || 0)) > 0 && (
                  <li>
                    <span className="font-medium">
                      {(usage.cpdRequests || 0) + (usage.cpdRecords || 0)} External CPD{' '}
                      {((usage.cpdRequests || 0) + (usage.cpdRecords || 0)) === 1 ? 'row' : 'rows'}
                    </span>
                    {' '}(declarations and records) {((usage.cpdRequests || 0) + (usage.cpdRecords || 0)) === 1 ? 'is' : 'are'} filed under it
                  </li>
                )}
              </ul>
              {record.active === false ? (
                <p className="text-xs">
                  It is already deactivated — nobody can pick it on the Sessions or External CPD
                  forms any more, and the rows above keep it. Leave it on the list as history.
                </p>
              ) : (
                <p className="text-xs">
                  Deactivate it instead: it disappears from the Sessions and External CPD forms so
                  nobody can pick it again, while every linked row keeps its classification exactly
                  as it is today. You can switch it back on at any time from the Actions column.
                </p>
              )}
            </div>
          )}
          {isSession && !usageLoading && !usageError && !hasChildren && (
            <p className="text-xs text-slate-500">
              No attendance, absence or feedback records or open declarations are linked — only the
              expected list and the QR check-in link are removed with the session.
              {(usage?.quizAttempts || 0) > 0 && (
                <span className="block mt-1 text-amber-700">
                  {usage.quizAttempts} quiz sitting{usage.quizAttempts === 1 ? '' : 's'} recorded against it
                  {usage.quizAttempts === 1 ? ' is' : ' are'} deleted with it too.
                </span>
              )}
              {(usage?.materials || 0) > 0 && (
                <span className="block mt-1 text-amber-700">
                  {usage.materials} course material{usage.materials === 1 ? '' : 's'} / recording{usage.materials === 1 ? '' : 's'} added to it
                  {usage.materials === 1 ? ' is' : ' are'} deleted with it too.
                </span>
              )}
            </p>
          )}
          {hasChildren && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-800 space-y-2">
              <p className="font-semibold">
                This also permanently deletes everything recorded against the session:
              </p>
              <ul className="text-xs space-y-0.5 list-disc pl-5">
                {(usage.attendance || 0) > 0 && (
                  <li>
                    <span className="font-medium">{usage.attendance} attendance record{usage.attendance === 1 ? '' : 's'}</span>
                    {' '}— these hours leave Training Hours and Mandatory Compliance retroactively
                  </li>
                )}
                {(usage.absences || 0) > 0 && (
                  <li>
                    <span className="font-medium">{usage.absences} absence reason{usage.absences === 1 ? '' : 's'}</span>
                    {(usage.absenceFiles || 0) > 0 && <> (incl. {usage.absenceFiles} evidence file{usage.absenceFiles === 1 ? '' : 's'})</>}
                  </li>
                )}
                {(usage.feedback || 0) > 0 && (
                  <li><span className="font-medium">{usage.feedback} feedback response{usage.feedback === 1 ? '' : 's'}</span></li>
                )}
                {(usage.quizAttempts || 0) > 0 && (
                  <li><span className="font-medium">{usage.quizAttempts} quiz sitting{usage.quizAttempts === 1 ? '' : 's'}</span> (scores and answer sheets)</li>
                )}
                {(usage.materials || 0) > 0 && (
                  <li><span className="font-medium">{usage.materials} course material{usage.materials === 1 ? '' : 's'} / recording{usage.materials === 1 ? '' : 's'}</span> (uploaded files and links)</li>
                )}
                {(usage.openAbsenceRequests || 0) > 0 && (
                  <li>
                    <span className="font-medium">{usage.openAbsenceRequests} open absence declaration{usage.openAbsenceRequests === 1 ? '' : 's'}</span>
                    {' '}— not yet approved, deleted with their attachments
                  </li>
                )}
                {(usage.openDisputeRequests || 0) > 0 && (
                  <li>
                    <span className="font-medium">{usage.openDisputeRequests} open attendance dispute{usage.openDisputeRequests === 1 ? '' : 's'}</span>
                    {' '}— not yet approved, deleted with their attachments
                  </li>
                )}
                <li>The expected list and the QR check-in / feedback / quiz links</li>
              </ul>
              <label className="flex items-start gap-2 pt-1 cursor-pointer">
                <input
                  type="checkbox"
                  checked={ackCascade}
                  onChange={e => setAckCascade(e.target.checked)}
                  className="mt-0.5 h-4 w-4 shrink-0 text-red-600 focus:ring-red-500 border-slate-300 rounded"
                />
                <span className="text-xs font-medium">
                  I understand this deletes the session AND all the records listed above.
                </span>
              </label>
            </div>
          )}

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            {categoryInUse || venueInUse ? (
              // Only offer the switch when it is still on — an already-retired
              // entry that cannot be deleted has nothing left to press.
              record.active !== false && (
                <button
                  onClick={deactivate}
                  disabled={deactivating}
                  className="px-3 py-2 bg-amber-600 text-white text-sm rounded hover:bg-amber-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {deactivating ? 'Deactivating…' : 'Deactivate Instead'}
                </button>
              )
            ) : (
              <button
                onClick={confirmDelete}
                disabled={deleting || usageLoading || (hasChildren && !ackCascade)}
                className="px-3 py-2 bg-red-600 text-white text-sm rounded hover:bg-red-700 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {deleting ? 'Deleting…' : (hasChildren ? 'Delete Session & Records' : 'Delete')}
              </button>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

// Mark as completed — the one-way door.
// Deliberately heavier than a yes/no: there is no reopen, so the dialog spells
// out both halves (what freezes, what still works) and demands an explicit tick
// before the button lights up. The preview count comes from the same expected
// list the snapshot will be taken from.
const CompleteSessionModal = ({ session, onClose, onDone }) => {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [ack, setAck] = useState(false);
  const [people, setPeople] = useState(null);   // null = still counting

  useEffect(() => {
    let active = true;
    fetchJSON(`/api/ld/sessions/${session.id}/roster`)
      .then(d => { if (active) setPeople((d.data || []).length); })
      .catch(() => { if (active) setPeople(-1); });   // -1 = could not count
    return () => { active = false; };
  }, [session.id]);

  const confirm = async () => {
    setSaving(true);
    setError(null);
    try {
      const result = await fetchJSON(`/api/ld/sessions/${session.id}/complete`, { method: 'POST' });
      onDone(result.snapshot ?? 0);
    } catch (e) { setError(e.message); }
    finally { setSaving(false); }
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">Mark Session as Completed</h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          <p className="text-sm text-slate-700">
            <span className="font-semibold">{session.courseTitle}</span>
            {session.date ? ` — ${formatDate(session.date)}` : ''}
          </p>

          <div className="p-3 bg-slate-50 border border-slate-200 rounded-lg text-sm text-slate-700 space-y-2">
            <p className="font-medium">
              What This Freezes, Permanently
            </p>
            <ul className="text-xs space-y-1 list-disc pl-5">
              <li>The session record — course, date, time, hours, the trainers with their factors, and the rules behind the expected list.</li>
              <li>
                The attendee list. Everyone on it{people > 0 ? ` (${people} people)` : ''} has their
                designation and department written down as they stand today, and every report about
                this session reads that copy from now on — later staff changes cannot move it.
              </li>
              <li>Check-in. It closes now and cannot be opened again, so nobody can scan themselves onto the list.</li>
            </ul>
          </div>

          <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-xs text-green-800">
            <span className="font-medium">Still possible afterwards:</span> adding, correcting or
            deleting attendance, absence and feedback records — but only for people already on the
            list. Someone who joins the firm later can no longer be added to this session.
          </div>

          <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-800">
            There is no way to undo this. A session completed by mistake has to be deleted and
            rebuilt from scratch, which takes its attendance records with it.
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input
              type="checkbox"
              checked={ack}
              onChange={e => setAck(e.target.checked)}
              className="mt-0.5 h-4 w-4 shrink-0 text-green-700 focus:ring-green-600 border-slate-300 rounded"
            />
            <span className="text-xs font-medium text-slate-700">
              I understand this cannot be undone.
            </span>
          </label>

          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}

          <div className="flex justify-end gap-2 pt-2">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
            <button
              onClick={confirm}
              disabled={saving || !ack}
              className="px-3 py-2 bg-green-700 text-white text-sm rounded hover:bg-green-800 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {saving ? 'Completing…' : 'Mark as Completed'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

// The live check-in list marks EXCEPTIONS only — icon, no
// text, because the list is scanned down the names while people arrive and
// prose on every row would bury the two rows worth looking at:
//
//   scan, landed outside the venue  → amber warning
//   scan, phone gave no position    → grey struck-through pin
//   scan, at the venue              → nothing (the expected case is silent)
//   typed in by hand / batch / etc. → nothing (GPS was never in its story)
//
// Same three-valued verdict as VenueCheckBadge, different job: the badge
// reports on a row being read one at a time, this flags a row worth stopping
// at in a list being skimmed.
const CheckinFlag = ({ attendee }) => {
  if (attendee.source !== 'checkin') return null;
  if (!attendee.hasReading) {
    return (
      <MapPinOff
        className="w-3.5 h-3.5 shrink-0 text-slate-400"
        aria-label="Checked in by QR, but the phone gave no location"
        title="Checked in by QR, but the phone gave no location — location was off or denied."
      />
    );
  }
  if (attendee.checkinInRange === false) {
    return (
      <AlertTriangle
        className="w-3.5 h-3.5 shrink-0 text-amber-500"
        aria-label="Scanned outside the venue"
        title="Scanned outside the venue radius."
      />
    );
  }
  return null;
};

// Which people the panel under the head-count tiles lists. Each tile is a
// tab; the panel shows the names behind the number.
const CHECKIN_TABS = {
  expected: { label: 'Expected', empty: 'Nobody on the list yet.' },
  checkedIn: { label: 'Checked In', empty: 'No one has checked in yet.' },
  walkIns: { label: 'Walk-Ins', empty: 'No walk-ins.' },
  absent: { label: 'Absent (Filed)', empty: 'No absence filed.' },
  response: { label: 'Response', empty: 'Nobody on the list yet.' },
};

// QR / check-in management for one session (docs/modules/ld-selfservice.md):
// open/close self check-in, show the QR + URL (QR is generated locally with
// the `qrcode` package — no external service), the head count with the
// people behind each tile, and the session letters.
const CheckinModal = ({ session, staffOptions = [], onClose }) => {
  const [state, setState] = useState(null);       // { session, attendees, stats }
  const [qrDataUrl, setQrDataUrl] = useState(null);
  const [error, setError] = useState(null);
  const [notice, setNotice] = useState(null);
  const [acting, setActing] = useState(false);
  const [copied, setCopied] = useState(false);
  // One picker drives all three letters: course
  // announcement, absence reminder, feedback request. `mail.kind` says which.
  const [mail, setMail] = useState(null);   // { kind, rows, selected:Set }
  // Expected-list management: the list is bound per person;
  // HR adjusts it here. Regenerate re-derives the rule-sourced rows only.
  const [roster, setRoster] = useState([]);
  // Which tile's people the panel lists. Checked In by default: while a
  // session runs, who has turned up is what HR watches.
  const [tab, setTab] = useState('checkedIn');
  const [addStaffId, setAddStaffId] = useState('');
  const [confirmRegen, setConfirmRegen] = useState(false);

  // The check-in URL must open the FRONTEND on the scanning device, so it is
  // built from this browser's own origin (works unchanged after deployment).
  const checkinUrl = state?.session?.checkinToken
    ? `${window.location.origin}/checkin/${state.session.checkinToken}`
    : null;

  // The roster rides along with every refresh: the Expected, Absent and
  // Response tabs are read from it, so it has to be as live as the scans.
  const load = useCallback(async () => {
    try {
      const [data, rosterData] = await Promise.all([
        fetchJSON(`/api/ld/sessions/${session.id}/checkin`),
        fetchJSON(`/api/ld/sessions/${session.id}/roster`),
      ]);
      setState(data);
      setRoster(rosterData.data || []);
      setError(null);
    } catch (e) { setError(e.message); }
  }, [session.id]);

  useEffect(() => { load(); }, [load]);

  // Live attendee list: people scan while the modal is open, so poll instead of
  // making HR close and reopen it. 5s is gentle enough for the global rate
  // limit; pause while the tab is hidden.
  useEffect(() => {
    const timer = setInterval(() => {
      if (!document.hidden) load();
    }, CHECKIN_POLL_MS);
    return () => clearInterval(timer);
  }, [load]);

  useEffect(() => {
    if (!checkinUrl) { setQrDataUrl(null); return; }
    QRCode.toDataURL(checkinUrl, { width: 480, margin: 1 })
      .then(setQrDataUrl)
      .catch((e) => console.error('QR generation failed:', e));
  }, [checkinUrl]);

  const toggleOpen = async () => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/sessions/${session.id}/checkin`, {
        method: 'POST',
        body: JSON.stringify({ open: !state.session.checkinOpen }),
      });
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(checkinUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch (e) { /* clipboard unavailable — the URL is selectable */ }
  };

  // -- Session mail picker -------------------------------------------------
  // Every send goes through the same three steps: load the candidates (with
  // status and when they were last sent this kind), tick, send. Nothing is
  // pre-selected beyond "the people who have not had this letter yet" - one
  // careless Select all is 200 emails.
  // Rows flagged `done` by the server (absence reason filed, feedback in,
  // quiz submitted) stay on the list greyed out: they cannot be ticked, the
  // quick-select buttons skip them, and the server drops them anyway.
  const openMail = async (kind) => {
    setActing(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/ld/sessions/${session.id}/mail/${kind}`);
      const rows = data.data || [];
      const fresh = rows.filter(r => !r.lastSentAt && !r.done);
      setMail({
        kind,
        rows,
        selected: new Set(fresh.map(r => r.staffId)),
      });
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const mailRows = mail ? mail.rows : [];
  const mailSendable = mailRows.filter(r => !r.done);

  const setMailSelection = (ids) => setMail(m => ({ ...m, selected: new Set(ids) }));

  const toggleMailRow = (staffId) => setMail((m) => {
    if (m.rows.some(r => r.staffId === staffId && r.done)) return m;
    const next = new Set(m.selected);
    if (next.has(staffId)) next.delete(staffId); else next.add(staffId);
    return { ...m, selected: next };
  });

  const sendMail = async () => {
    setActing(true);
    setError(null);
    try {
      const data = await fetchJSON(`/api/ld/sessions/${session.id}/mail/${mail.kind}`, {
        method: 'POST',
        body: JSON.stringify({ staffIds: Array.from(mail.selected) }),
      });
      setNotice(`${MAIL_LABELS[mail.kind]} sent to ${data.sent} of ${data.selected} selected.`);
      setMail(null);
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const addRosterMember = async () => {
    if (!addStaffId) return;
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/sessions/${session.id}/roster`, {
        method: 'POST',
        body: JSON.stringify({ staffId: Number(addStaffId) }),
      });
      setAddStaffId('');
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const removeRosterMember = async (staffId) => {
    setActing(true);
    setError(null);
    try {
      await fetchJSON(`/api/ld/sessions/${session.id}/roster/${staffId}`, { method: 'DELETE' });
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const regenerateRoster = async () => {
    setActing(true);
    setError(null);
    try {
      const result = await fetchJSON(`/api/ld/sessions/${session.id}/roster/regenerate`, { method: 'POST' });
      setConfirmRegen(false);
      setNotice(`Expected list re-derived from the session's rules${
        typeof result.added === 'number' ? ` (${result.added} on the list)` : ''
      }. Manual additions and people with records were kept.`);
      await load();
    } catch (e) { setError(e.message); }
    finally { setActing(false); }
  };

  const s = state?.session;

  // The people behind each tile. 'attend' roster rows are walk-ins: put on
  // the roster by a record so later steps can find them, but never expected,
  // so they belong on the Walk-Ins tab and nowhere else.
  const attendees = state?.attendees || [];
  const expectedRows = roster.filter((m) => m.source !== 'attend');
  const tabRows = {
    expected: expectedRows,
    checkedIn: attendees.filter((a) => a.onList),
    walkIns: attendees.filter((a) => !a.onList),
    absent: roster.filter((m) => m.absent && !m.isTrainer),
    response: expectedRows.filter((m) => !m.isTrainer),
  };
  // The Walk-Ins tile only exists while there is one; fall back if it goes.
  const activeTab = tab === 'walkIns' && !(state?.stats?.walkIns > 0) ? 'checkedIn' : tab;
  const rows = tabRows[activeTab] || [];

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-5 border-b border-slate-200">
          <h3 className="text-lg font-semibold text-slate-900">
            QR Check-In — {session.courseTitle}
          </h3>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600"><X className="w-5 h-5" /></button>
        </div>
        <div className="p-5 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
              {error}
            </div>
          )}
          {notice && (
            <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-green-700 text-sm">
              {notice}
            </div>
          )}

          {!state ? (
            <p className="text-sm text-slate-500 text-center py-6">Loading…</p>
          ) : (
            <>
              {/* Status + toggle */}
              <div className="flex flex-wrap items-center justify-between gap-3">
                <div className="flex items-center gap-2 flex-wrap">
                  <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${s.checkinOpen ? 'bg-green-50 text-green-700' : 'bg-slate-100 text-slate-500'}`}>
                    {s.checkinOpen ? 'Check-in open' : 'Check-in closed'}
                  </span>
                  {/* Completed = frozen for good. Check-in is the one
                      thing completion stops outright: a scan would put a new
                      name on a list that is supposed to be final. */}
                  {s.completed && (
                    <span className="text-xs text-slate-500">
                      Session completed — check-in cannot be opened again.
                    </span>
                  )}
                </div>
                <LockTip text={s.completed && !s.checkinOpen ? 'This session is marked completed — check-in is closed for good' : null} align="right">
                  <button
                    onClick={toggleOpen}
                    disabled={acting || (s.completed && !s.checkinOpen)}
                    className={`px-3 py-2 text-sm font-medium rounded-lg disabled:opacity-50 disabled:cursor-not-allowed ${
                      s.checkinOpen
                        ? 'border border-slate-300 text-slate-700 hover:bg-slate-50'
                        : 'bg-blue-600 text-white hover:bg-blue-700'
                    }`}
                  >
                    {s.checkinOpen ? 'Close Check-In' : 'Open Check-In'}
                  </button>
                </LockTip>
              </div>

              {/* Head count — every tile is a tab; the panel below lists
                  the people behind the active one. */}
              {state.stats && (
                <div>
                  {/* The Walk-Ins tile appears only once there is one — a
                      permanent "0" would just be noise on the usual session. */}
                  <div className={`grid grid-cols-2 gap-2 ${state.stats.walkIns > 0 ? 'sm:grid-cols-5' : 'sm:grid-cols-4'}`}>
                    <StatTile
                      label="Expected"
                      value={state.stats.expected}
                      tone="slate"
                      active={activeTab === 'expected'}
                      onClick={() => setTab('expected')}
                    />
                    {/* Checked In counts only the expected people, so "50 of 50"
                        really means everyone planned turned up. Walk-ins —
                        scans by people who were not on the list — get their
                        own tile and never pad this one. Amber while some of
                        the expected list is still missing. */}
                    <StatTile
                      label="Checked In"
                      value={`${state.stats.attendedExpected}/${state.stats.expected}`}
                      tone={state.stats.expected === 0 ? 'slate'
                        : (state.stats.attendedExpected >= state.stats.expected ? 'green' : 'amber')}
                      active={activeTab === 'checkedIn'}
                      onClick={() => setTab('checkedIn')}
                    />
                    {state.stats.walkIns > 0 && (
                      <StatTile
                        label="Walk-Ins"
                        value={state.stats.walkIns}
                        tone="slate"
                        hint={SESSION_STAT_HINTS.walkIns}
                        active={activeTab === 'walkIns'}
                        onClick={() => setTab('walkIns')}
                      />
                    )}
                    <StatTile
                      label="Absent (Filed)"
                      value={state.stats.absent}
                      tone="amber"
                      active={activeTab === 'absent'}
                      onClick={() => setTab('absent')}
                    />
                    {/* Answered (checked in or filed an absence) out of the expected
                        list — the same wording as the session detail panel. */}
                    <StatTile
                      label="Response"
                      value={`${state.stats.expected - state.stats.pending}/${state.stats.expected}`}
                      tone="slate"
                      active={activeTab === 'response'}
                      onClick={() => setTab('response')}
                    />
                  </div>
                  {state.stats.expected === 0 && (
                    <p className="text-xs text-amber-600 mt-1">
                      The expected list is empty — open Expected to add people or re-derive it.
                    </p>
                  )}
                </div>
              )}

              {/* The people behind the active tile */}
              <div className="border border-slate-200 rounded-lg">
                <div className="flex flex-wrap items-center justify-between gap-2 px-4 py-2.5 border-b border-slate-100">
                  <p className="text-sm font-medium text-slate-800">
                    {CHECKIN_TABS[activeTab].label}
                    <span className="ml-1.5 text-slate-400 font-normal">{rows.length}</span>
                  </p>
                  {/* Nothing re-derives on a completed session — the
                      list IS the record at that point. */}
                  {activeTab === 'expected' && !s.completed && (
                    confirmRegen ? (
                      <span className="inline-flex items-center gap-1.5 text-xs">
                        {/* Rules like "missed session X" or "below target" are
                            answered against TODAY's data, so a re-run can
                            legitimately produce a different list. */}
                        <span className="text-amber-700">Re-derive from the session&apos;s rules, using today&apos;s data?</span>
                        <button onClick={regenerateRoster} disabled={acting} className="px-2 py-1 bg-amber-600 text-white rounded disabled:opacity-50">Yes</button>
                        <button onClick={() => setConfirmRegen(false)} className="px-2 py-1 border border-slate-300 text-slate-600 rounded">No</button>
                      </span>
                    ) : (
                      <button
                        onClick={() => setConfirmRegen(true)}
                        disabled={acting}
                        title="Re-derive the rule-based rows; manual additions and recorded people are kept"
                        className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 disabled:opacity-50"
                      >
                        <RefreshCw className="w-3.5 h-3.5" /> Regenerate
                      </button>
                    )
                  )}
                </div>
                <div className="p-4 space-y-2">
                  {activeTab === 'expected' && (
                    s.completed ? (
                      <p className="text-xs text-slate-500">
                        This session is completed — the list is frozen. Attendance, absences and
                        feedback can still be filed, but only for the people below.
                      </p>
                    ) : (
                      <div className="flex gap-2">
                        <div className="flex-1 min-w-0">
                          <SearchSelect
                            value={addStaffId}
                            onChange={setAddStaffId}
                            options={staffOptions
                              // A walk-in ('attend' row) can still be added: that is
                              // how HR moves them onto the expected list after the fact.
                              .filter((st) => !roster.some((r) => r.staffId === st.id && r.source !== 'attend'))
                              .map((st) => ({ value: st.id, label: st.name, group: st.position || 'No designation' }))}
                            placeholder="Add a person…"
                          />
                        </div>
                        <button
                          onClick={addRosterMember}
                          disabled={acting || !addStaffId}
                          className="inline-flex items-center gap-1.5 px-3 py-2 bg-blue-600 text-white text-sm rounded-lg hover:bg-blue-700 disabled:opacity-50 shrink-0"
                        >
                          <UserPlus className="w-4 h-4" /> Add
                        </button>
                      </div>
                    )
                  )}

                  {rows.length === 0 ? (
                    <p className="text-sm text-slate-500">{CHECKIN_TABS[activeTab].empty}</p>
                  ) : (
                    <ul className="max-h-64 overflow-y-auto divide-y divide-slate-100 border border-slate-100 rounded-lg">
                      {activeTab === 'expected' && rows.map((m) => (
                        <li key={m.staffId} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <span className="text-slate-800 truncate">{m.name}</span>
                          <span className="text-xs text-slate-600 truncate">{m.department}</span>
                          {m.isTrainer && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-blue-50 text-blue-700">Trainer</span>
                          )}
                          {m.source === 'manual' && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-slate-100 text-slate-500">added</span>
                          )}
                          {/* Why the rules put them here —
                              with several rules feeding one list, the source
                              alone is not an explanation. */}
                          {m.source === 'rule' && m.ruleLabel && (
                            <span className="px-1.5 py-0.5 text-xs rounded bg-slate-100 text-slate-500 truncate max-w-[12rem]" title={m.ruleLabel}>
                              {m.ruleLabel}
                            </span>
                          )}
                          {m.source && !m.isTrainer && !s.completed && (
                            <button
                              onClick={() => removeRosterMember(m.staffId)}
                              disabled={acting}
                              title="Remove from the expected list"
                              className="ml-auto p-1 text-slate-400 hover:text-red-600 disabled:opacity-50"
                            >
                              <X className="w-3.5 h-3.5" />
                            </button>
                          )}
                        </li>
                      ))}
                      {(activeTab === 'checkedIn' || activeTab === 'walkIns') && rows.map((a) => (
                        <li key={a.staffId} className="px-3 py-1.5 text-sm text-slate-700 flex items-center justify-between gap-2">
                          <span className="flex items-center gap-1.5 min-w-0">
                            <span className="truncate">{a.name}</span>
                            <CheckinFlag attendee={a} />
                          </span>
                          {/* When they scanned, not what it was worth:
                              every row on this list is the same
                              session's hours, so "2h" would say nothing — the
                              live list is watched to see people arriving. */}
                          <span className="text-xs text-slate-500 shrink-0">
                            {a.checkedInAt ? formatDateTime(a.checkedInAt) : '—'}
                          </span>
                        </li>
                      ))}
                      {activeTab === 'absent' && rows.map((m) => (
                        <li key={m.staffId} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <span className="text-slate-800 truncate">{m.name}</span>
                          <span className="text-xs text-slate-600 truncate">{m.department}</span>
                        </li>
                      ))}
                      {activeTab === 'response' && rows.map((m) => (
                        <li key={m.staffId} className="flex items-center gap-2 px-3 py-1.5 text-sm">
                          <span className="text-slate-800 truncate">{m.name}</span>
                          <span className="text-xs text-slate-600 truncate">{m.department}</span>
                          <span className={`ml-auto px-1.5 py-0.5 text-xs rounded shrink-0 ${
                            m.attended
                              ? 'bg-green-50 text-green-700'
                              : m.absent
                                ? 'bg-amber-50 text-amber-700'
                                : 'bg-slate-100 text-slate-500'}`}>
                            {m.attended ? 'Checked in' : m.absent ? 'Absence filed' : 'No response'}
                          </span>
                        </li>
                      ))}
                    </ul>
                  )}
                  {/* Same note as the session detail panel: a trainer row
                      left over from an old list is shown but never counted,
                      so this list can be one longer than Expected. */}
                  {activeTab === 'expected' && rows.some((m) => m.isTrainer) && (
                    <p className="text-xs text-slate-500">
                      The trainer is shown for reference only — they teach the session, so they
                      count in none of the tiles above and are not a checked-in participant.
                    </p>
                  )}
                </div>
              </div>

              {/* QR + URL */}
              {s.checkinToken ? (
                <div className="border border-slate-200 rounded-lg p-4 bg-slate-50">
                  <div className="flex flex-col sm:flex-row items-center gap-4">
                    {qrDataUrl && (
                      <img
                        src={qrDataUrl}
                        alt="Check-in QR code"
                        className={`w-56 h-56 bg-white rounded-lg border border-slate-200 p-2 ${s.checkinOpen ? '' : 'opacity-40'}`}
                      />
                    )}
                    <div className="flex-1 min-w-0 space-y-2 text-center sm:text-left">
                      <p className="text-sm text-slate-700">
                        Project or print this QR code at the venue — participants scan it,
                        sign in, and are recorded as attended.
                      </p>
                      <div className="flex items-center gap-2 justify-center sm:justify-start">
                        <code className="text-xs bg-white border border-slate-200 rounded px-2 py-1.5 truncate max-w-[220px]" title={checkinUrl}>
                          {checkinUrl}
                        </code>
                        <button
                          onClick={copyUrl}
                          className="inline-flex items-center gap-1 px-2 py-1.5 text-xs border border-slate-300 rounded hover:bg-white text-slate-600"
                        >
                          <Copy className="w-3.5 h-3.5" /> {copied ? 'Copied!' : 'Copy URL'}
                        </button>
                      </div>
                      {/* No regenerate action: the QR is
                          printed and circulated days ahead, so the link is
                          permanent — rotating it would kill paper already on
                          the noticeboard. */}
                      <p className="text-xs text-slate-500">
                        This link is permanent — print or circulate it as early as you like.
                      </p>
                      {!s.checkinOpen && (
                        <p className="text-xs text-amber-600">Check-in is closed — scans will be refused until you open it.</p>
                      )}
                    </div>
                  </div>
                </div>
              ) : (
                <p className="text-sm text-slate-500">
                  This session has no check-in link yet — reload the page; every session gets one
                  when it is created.
                </p>
              )}

              {/* Emails — one card per letter: name on one line, send status
                  on the next. What each letter is for is explained once the
                  card is opened (MAIL_PICKER_HINT), not on the card. */}
              <div className="border-t border-slate-100 pt-4">
                <h4 className={`${subsectionTitle} mb-2`}>
                  <span className={subsectionTitleBar} aria-hidden="true" />
                  Emails for This Session
                </h4>
                <div className="grid gap-2 grid-cols-2 lg:grid-cols-4">
                  {MAIL_ACTIONS.map((a) => {
                    const Icon = a.icon;
                    const sum = (state.mailSummary || []).find((m) => m.kind === a.kind);
                    const active = mail?.kind === a.kind;
                    // The quiz letter has nobody to go to until a quiz is
                    // attached on the session form — the card stays, greyed,
                    // so the option is visible.
                    const unavailable = a.needsQuiz && !state.session.quizId;
                    return (
                      <button
                        key={a.kind}
                        onClick={() => openMail(a.kind)}
                        disabled={acting || unavailable}
                        className={`text-left border rounded-lg px-3 py-2.5 transition-colors ${
                          unavailable
                            ? 'border-slate-200 bg-slate-50 cursor-default'
                            : active
                              ? 'border-blue-500 bg-blue-50 ring-1 ring-blue-500'
                              : 'border-slate-200 hover:border-blue-400 hover:bg-blue-50/60 disabled:opacity-50'}`}
                      >
                        <span className={`flex items-center gap-2 text-sm font-medium whitespace-nowrap ${
                          unavailable ? 'text-slate-400' : 'text-slate-800'}`}>
                          <Icon className={`w-4 h-4 shrink-0 ${
                            unavailable ? 'text-slate-300' : active ? 'text-blue-600' : 'text-slate-400'}`} />
                          <span className="truncate">{a.label}</span>
                        </span>
                        <span className={`block text-xs mt-1 truncate ${
                          unavailable ? 'text-slate-400' : sum ? 'text-slate-600' : 'text-slate-500'}`}>
                          {unavailable
                            ? 'No quiz on this session'
                            : sum
                              ? `Sent to ${sum.people}${sum.lastSentAt ? ` · ${formatDate(sum.lastSentAt)}` : ''}`
                              : 'Not sent yet'}
                        </span>
                      </button>
                    );
                  })}
                </div>
              </div>

              {/* Recipient picker (shared by the three letters) */}
              {mail && (
                <div className="border border-slate-200 rounded-lg p-4">
                  <div className="flex items-center justify-between mb-2">
                    <h4 className="text-sm font-semibold text-slate-900">
                      {MAIL_LABELS[mail.kind]} — {mail.selected.size} of {mailSendable.length} selected
                    </h4>
                    <div className="flex gap-3 text-xs">
                      <button
                        onClick={() => setMailSelection(mailSendable.filter(r => !r.lastSentAt).map(r => r.staffId))}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Not Sent Yet
                      </button>
                      <button
                        onClick={() => setMailSelection(mailSendable.map(r => r.staffId))}
                        className="text-blue-600 hover:text-blue-800"
                      >
                        Select All
                      </button>
                      <button onClick={() => setMailSelection([])} className="text-blue-600 hover:text-blue-800">
                        Clear
                      </button>
                    </div>
                  </div>
                  <p className="text-xs text-slate-500 mb-2">{MAIL_PICKER_HINT[mail.kind]}</p>
                  {mailRows.length === 0 ? (
                    <p className="text-sm text-slate-500">Nobody to write to here.</p>
                  ) : (
                    <ul className="max-h-56 overflow-y-auto divide-y divide-slate-100 border border-slate-200 rounded-lg mb-3">
                      {mailRows.map((r) => (
                        <li key={r.staffId}>
                          <label
                            className={`flex items-center gap-2 px-3 py-1.5 text-sm ${
                              r.done
                                ? 'text-slate-400 bg-slate-50/60 cursor-default'
                                : 'text-slate-700 hover:bg-slate-50 cursor-pointer'}`}
                          >
                            <input
                              type="checkbox"
                              checked={mail.selected.has(r.staffId)}
                              disabled={r.done}
                              onChange={() => toggleMailRow(r.staffId)}
                            />
                            <span className="flex-1 truncate">{r.name}</span>
                            {/* Attended without being on the expected list — the
                                feedback and quiz letters reach them all the same. */}
                            {r.attended && r.onList === false && (
                              <span className="px-1.5 py-0.5 text-xs rounded bg-amber-50 text-amber-800">walk-in</span>
                            )}
                            {r.absenceFiled && (
                              <span className="px-1.5 py-0.5 text-xs rounded bg-amber-50 text-amber-700">absence filed</span>
                            )}
                            {mail.kind === 'feedback_request' && r.feedbackFiled && (
                              <span className="px-1.5 py-0.5 text-xs rounded bg-green-50 text-green-700">feedback in</span>
                            )}
                            {mail.kind === 'quiz_request' && r.quizDone && (
                              <span className="px-1.5 py-0.5 text-xs rounded bg-green-50 text-green-700">quiz passed</span>
                            )}
                            <span className="text-xs text-slate-500 shrink-0">
                              {r.lastSentAt ? `sent ${formatDate(r.lastSentAt)}` : 'never sent'}
                            </span>
                          </label>
                        </li>
                      ))}
                    </ul>
                  )}
                  <div className="flex justify-end gap-2">
                    <button onClick={() => setMail(null)} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Cancel</button>
                    <button
                      onClick={sendMail}
                      disabled={acting || mail.selected.size === 0}
                      className="px-3 py-2 bg-blue-600 text-white text-sm rounded hover:bg-blue-700 disabled:opacity-50"
                    >
                      {acting ? 'Sending…' : `Send to ${mail.selected.size}`}
                    </button>
                  </div>
                </div>
              )}
            </>
          )}

          <div className="flex justify-end border-t border-slate-100 pt-3">
            <button onClick={onClose} className="px-3 py-2 text-sm text-slate-600 hover:bg-slate-100 rounded">Close</button>
          </div>
        </div>
      </div>
    </div>
  );
};

// Compact count tile for the check-in modal (same slate/green/amber language
// as the rest of the page — no new palette).
// Label + control + hint for the record modal. Inside a FormValidationProvider
// whose gap list names `field`, it marks itself the way a FormKit Field does —
// red label, red halo, "Required" under the control — and carries the data
// attribute the modal scrolls to.
const FieldRow = ({ label, hint, field = null, children }) => {
  const invalid = useFieldInvalid(field);
  return (
    <div className={invalid ? fieldInvalidRing : undefined} {...(invalid ? { [INVALID_FIELD_ATTR]: field } : {})}>
      <label className={`block text-sm font-medium mb-1.5 ${invalid ? fieldInvalidLabel : 'text-slate-700'}`}>{label}</label>
      {children}
      {hint && <p className="text-xs text-slate-500 mt-1">{hint}</p>}
      {invalid && <p className={fieldInvalidText}>Required</p>}
    </div>
  );
};

// Read-only session detail: who attended, who was away and
// why, and the feedback — the very panel the dashboard's Training Sessions tab
// expands into, reached from the Sessions row's View action so HR does not have
// to leave the records page to answer "who was actually there?".
const SessionDetailModal = ({ session, onClose }) => {
  const [detail, setDetail] = useState(null);
  const [error, setError] = useState(null);
  const [tab, setTab] = useState('roster');

  const load = useCallback(async () => {
    const data = await fetchJSON(`/api/ld/training-sessions/${session.id}/detail`);
    setDetail(data);
  }, [session.id]);

  useEffect(() => {
    let cancelled = false;
    setDetail(null);
    setError(null);
    setTab('roster');
    load().catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [load]);

  // Quiz tab: wipe every sitting of one person so they start over at attempt
  // 1 (a retake after Not passed needs no HR action), then re-read the panel.
  const resetAttempt = async (staffId) => {
    await fetchJSON(`/api/ld/sessions/${session.id}/quiz-attempts/${staffId}`, { method: 'DELETE' });
    await load();
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900 truncate">
              {session.courseTitle || session.sessionCode}
            </h3>
            <p className="text-sm text-slate-500 truncate">
              {formatDate(session.date)}
              {session.categoryTitle ? ` · ${session.categoryTitle}` : ''}
              {session.isMandatory ? ' · Mandatory' : ''}
            </p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}
          {!error && !detail && <p className="text-sm text-slate-500">Loading…</p>}
          {detail && (
            <SessionDetailPanel detail={detail} tab={tab} onTab={setTab} onResetAttempt={resetAttempt} onFilesChange={load} />
          )}
        </div>
      </div>
    </div>
  );
};

// Per-record history — same idea as the Engagement history
// modal: an append-only timeline with a Field / Before / After table per
// update. Events survive deletion via the label snapshot.
const RecordHistoryModal = ({ type, tabConfig, record, onClose }) => {
  const [events, setEvents] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    fetchJSON(`/api/ld/records/${type}/${record.id}/events`)
      .then((data) => { if (!cancelled) setEvents(data.data || []); })
      .catch((e) => { if (!cancelled) setError(e.message); });
    return () => { cancelled = true; };
  }, [type, record.id]);

  // Field key → human label, from the tab's own form/column definitions.
  const labelOf = (key) => {
    const f = tabConfig.fields.find((x) => x.key === key);
    if (f) return f.label;
    const c = tabConfig.columns.find((x) => x.key === key);
    if (c) return c.label;
    return key.replace(/([A-Z])/g, ' $1').replace(/^./, (s) => s.toUpperCase());
  };
  // Objects only ever arrive here as a session's audience rules —
  // String() would print [object Object].
  const show = (v) => {
    if (v === null || v === undefined || v === '') return '—';
    if (typeof v === 'boolean') return v ? 'Yes' : 'No';
    if (typeof v === 'object') return summariseAudienceRules(v);
    // Stored diffs keep dates ISO; the timeline reads DD-MM-YYYY like everywhere else.
    if (typeof v === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(v)) return formatDate(v);
    return String(v);
  };

  const ACTION_STYLES = {
    created: 'bg-green-100 text-green-800',
    updated: 'bg-blue-100 text-blue-800',
    deleted: 'bg-red-100 text-red-800',
  };

  return (
    <div className="fixed inset-0 bg-slate-900/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[85vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-200">
          <div className="min-w-0">
            <h3 className="text-lg font-semibold text-slate-900">History</h3>
            <p className="text-sm text-slate-500 truncate">{tabConfig.title} #{record.id}</p>
          </div>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X className="w-5 h-5" />
          </button>
        </div>
        <div className="p-6 space-y-4">
          {error && (
            <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-700">{error}</div>
          )}
          {!error && events === null && (
            <p className="text-sm text-slate-500">Loading…</p>
          )}
          {events !== null && events.length === 0 && (
            <p className="text-sm text-slate-500">No history yet.</p>
          )}
          {(events || []).map((ev) => {
            const changeKeys = ev.changes ? Object.keys(ev.changes) : [];
            return (
              <div key={ev.id} className="border border-slate-200 rounded-lg p-4">
                <div className="flex flex-wrap items-center gap-2 text-sm">
                  <span className={`px-2 py-0.5 text-xs font-semibold rounded-full ${ACTION_STYLES[ev.action] || 'bg-slate-100 text-slate-700'}`}>
                    {ev.action}
                  </span>
                  <span className="font-medium text-slate-800 truncate">{ev.label}</span>
                  <span className="ml-auto text-xs text-slate-500">
                    {formatDateTime(ev.createdAt)}
                  </span>
                </div>
                <p className="text-xs text-slate-500 mt-1">
                  {ev.actorEmail || 'system'}
                  {ev.source && ev.source !== 'manual' && <span className="ml-1 text-slate-500">({ev.source})</span>}
                </p>
                {changeKeys.length > 0 && (
                  <table className="mt-2 w-full text-xs">
                    <thead>
                      <tr className="text-left text-slate-500 uppercase">
                        <th className="py-1 pr-2 font-medium">Field</th>
                        <th className="py-1 pr-2 font-medium">Before</th>
                        <th className="py-1 font-medium">After</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-slate-100">
                      {changeKeys.map((k) => (
                        <tr key={k}>
                          <td className="py-1 pr-2 font-medium text-slate-600">{labelOf(k)}</td>
                          <td className="py-1 pr-2 text-slate-500">{show(ev.changes[k].before)}</td>
                          <td className="py-1 text-slate-800">{show(ev.changes[k].after)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
};

// Deep-link compatibility for the three-way split: a `?tab=`
// naming a tab that now lives on another screen is sent there instead of being
// silently ignored. Keeps every bookmark and already-sent email link working —
// e.g. the attendance-dispute mail's /ld/records?tab=attendance.
const LDRecordsPage = ({ group = 'training' }) => {
  const [searchParams] = useSearchParams();
  const rawTab = searchParams.get('tab');
  const owner = RECORD_TABS.find(t => t.key === rawTab)?.group;

  if (owner && owner !== group) {
    return <Navigate to={`${getGroupConfig(owner).path}?tab=${rawTab}`} replace />;
  }
  return <LDRecords group={group} />;
};

export default LDRecordsPage;
